'use client';
// Store GED — session, navigation, données (documents / demandes / journal /
// paramétrage) et actions de transition (toutes déléguées au moteur Strapi).
import { create } from 'zustand';
import { refusDepot } from '@/lib/depot';
import { ADRESSES, CLES_ADRESSABLES, lireChemin } from '@/lib/adresse';
import { peutLirePlanning, planningSeul } from '@/lib/acces-planning';
import type { CleSelection, GedDocument, GedDemande, GedEvent, GedSettings, GedSession, GedView, GedNC, GedAction, GedReclamation, GedAudit, GedRdd, GedEquipement, GedFournisseur, GedLot, GedCqi, GedCqiCarteResume, RecapCqi, GedEeq, GedAnalyte, GedLien, GedCopie, GedEtiquette, ImpactSortieEquip, RhAcces, RhPersonnel, RhPoste, RhAffectation, RhAbsence, RhParametres, RhFerie, RhSolde, RhJournee, RhPointage, RapportImport, ConstatPointage, PropositionAbsence, SensSuspect, RhPaie, RhHabilitation, RhHabilitationDetenue, RhContrat, RhIntegration, RhIntegrationPhase, RhIntegrationPiece, RhOuverture, RhModeleOuverture, RhRegime, MaintenanceRegistre, AudienceApercu, AudienceRegle, PropositionCompte, ResultatCompte, RapprochementComptes } from '@/lib/types';

/** Lundi de la semaine d'une date — le planning se lit par semaine entière. */
export function lundiDe(d: Date): string {
  const x = new Date(d.getFullYear(), d.getMonth(), d.getDate());
  x.setDate(x.getDate() - ((x.getDay() + 6) % 7));
  return `${x.getFullYear()}-${String(x.getMonth() + 1).padStart(2, '0')}-${String(x.getDate()).padStart(2, '0')}`;
}

/** Les 7 jours d'une semaine à partir de son lundi. */
export function joursSemaine(lundi: string): string[] {
  const [a, m, j] = lundi.split('-').map(Number);
  return Array.from({ length: 7 }, (_, i) => {
    const d = new Date(a, m - 1, j + i);
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  });
}


/** Mois courant au format AAAA-MM. */
function moisCourant(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
}

/**
 * Un échec d'appel, AVEC ce qu'on sait de ses effets.
 *
 * `indetermine` est la seule information qui change ce que l'utilisateur doit
 * faire ensuite. Un refus du moteur (« Clôture impossible : … ») n'a rien écrit
 * et se corrige ; une écriture partie sans réponse a PEUT-ÊTRE été appliquée,
 * et refaire le geste crée un doublon. Les confondre sous un même « erreur »
 * revenait à conseiller de recliquer dans le seul cas où il ne faut pas.
 */
export class ErreurAppel extends Error {
  readonly indetermine: boolean;
  /** Code HTTP de la réponse du moteur (0 = pas de réponse). Permet à l'écran de
   *  distinguer un CONFLIT (409) d'un refus d'un autre ordre. */
  statut = 0;
  constructor(message: string, indetermine = false) {
    super(message);
    this.name = 'ErreurAppel';
    this.indetermine = indetermine;
  }
}

/** Garde-fou côté navigateur, volontairement PLUS LONG que celui du proxy
 *  (15 s en écriture, 30 s en lecture) : le proxy doit avoir le temps de rendre
 *  son propre message, qui sait si l'appel était une écriture — donc si le
 *  doute porte sur des données. Celui-ci ne sert que si le proxy lui-même ne
 *  répond pas. */
const DELAI_CLIENT_MS = 40_000;

async function api(path: string, init?: RequestInit) {
  let res: Response;
  try {
    res = await fetch(`/api/ged/${path}`, {
      credentials: 'include',
      signal: AbortSignal.timeout(DELAI_CLIENT_MS),
      ...init,
    });
  } catch (e) {
    // Ni réponse ni refus : le navigateur n'a pas pu joindre l'application
    // (réseau coupé, onglet en veille, proxy muet). Une écriture est alors
    // indéterminée — on ne sait pas si elle est partie.
    const ecriture = !!init?.method && init.method !== 'GET';
    const expire = e instanceof Error && (e.name === 'TimeoutError' || e.name === 'AbortError');
    throw new ErreurAppel(
      ecriture
        ? `${expire ? 'Aucune réponse dans le délai imparti' : 'Connexion interrompue'}. Votre saisie a peut-être été enregistrée : vérifiez le registre avant de refaire l’opération.`
        : `${expire ? 'Aucune réponse dans le délai imparti' : 'Connexion interrompue'}. Rien n’a été modifié — réessayez.`,
      ecriture,
    );
  }
  const data = await res.json().catch(() => null);
  if (!res.ok) {
    // 413 — la requête a été refusée AVANT d'atteindre le contrôleur : trop
    // grosse pour la limite de corps. Le message brut de Strapi est en anglais
    // (« request entity too large ») et ne dit ni la limite ni le poids ; le
    // middleware `charge-utile` le remplace côté serveur, mais si la coupure
    // vient d'un intermédiaire (proxy inverse, passerelle), il faut malgré tout
    // une phrase qui se lise. Ce n'est PAS une panne, et ça ne doit pas
    // s'afficher comme telle.
    if (res.status === 413) {
      const dit = data?.error?.message;
      throw new Error(typeof dit === 'string' && !/entity too large/i.test(dit)
        ? dit
        : 'Envoi refusé : fichier trop volumineux pour la plateforme (25 Mo maximum par fichier). Allégez-le, ou déposez-le en plusieurs pièces.');
    }
    const msg = data?.error?.message || data?.error || 'Erreur serveur';
    // Le proxy marque `indetermine` quand une ÉCRITURE est partie sans qu'il
    // sache si le moteur l'a appliquée (504/502). Ce drapeau voyage jusqu'au
    // toast : c'est lui qui décide si l'on propose « Réessayer » ou si l'on
    // demande d'abord d'aller vérifier.
    const err = new ErreurAppel(typeof msg === 'string' ? msg : 'Erreur serveur', data?.indetermine === true);
    err.statut = res.status;
    /* Les DÉTAILS du refus voyagent avec l'erreur (01/10/2026) : ils permettent
       à l'écran de proposer le remède — rouvrir la journée validée, par ex. */
    (err as { details?: unknown }).details = data?.error?.details;
    throw err;
  }
  return data;
}
// backend/config/api.ts impose maxLimit: 100 : une requête `pagination[limit]=500`
// est silencieusement ramenée à 100 lignes — sans erreur ni avertissement. On
// pagine donc au lieu de relever le plafond. Critique pour ged-events, la piste
// d'audit ISO 15189 : elle ne fait que croître et ne doit jamais se tronquer en
// silence. Passer `path` SANS pagination : elle est ajoutée ici.
const PAGE = 100;
async function getAll(path: string): Promise<never[]> {
  const out: unknown[] = [];
  const sep = path.includes('?') ? '&' : '?';
  for (let page = 1; page <= 100; page += 1) {
    const d = await api(`${path}${sep}pagination[page]=${page}&pagination[pageSize]=${PAGE}`).catch(() => null);
    const data: unknown[] = d?.data ?? [];
    out.push(...data);
    if (!data.length || page >= (d?.meta?.pagination?.pageCount ?? 1)) break;
  }
  return out as never[];
}
const post = (path: string, body?: unknown) => api(path, {
  method: 'POST', headers: { 'Content-Type': 'application/json' }, body: body ? JSON.stringify(body) : undefined,
});
const del = (path: string) => api(path, { method: 'DELETE' });

/* ── Assistance IA : classer un échec (lot GED-13) ──────────────────────────
 * Le moteur répond en HTTP 503 dans trois situations que rien ne distingue au
 * niveau du code : pas de serveur configuré, serveur qui ne répond pas, délai
 * dépassé. Seul le MESSAGE les sépare, et il est écrit une seule fois — dans
 * `backend/src/api/ia/controllers/ia.ts`. On s'appuie donc sur deux fragments
 * stables de ce fichier, comparés sans accent ni apostrophe typographique,
 * plutôt que sur la phrase entière.
 *
 * ⚠️ Si ces phrases changent côté moteur, l'écran retombe sur « panne » — état
 * le plus prudent des trois : il invite à signaler, jamais à attendre.
 */
const sansDiacritiques = (s: string) => s.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();
function motifDepuisReponse(statut: number, message: string): EchecIA {
  const m = sansDiacritiques(message);
  if (statut === 403) return 'droit';
  if (statut === 400) return 'refus';
  if (m.includes('pas configuree')) return 'eteint';
  if (m.includes('trop de temps')) return 'delai';
  return 'panne';
}
/** Échéance côté navigateur, volontairement PLUS LONGUE que celle du moteur
 *  (60 s par défaut) : le moteur doit avoir le temps de rendre son propre
 *  message de délai dépassé, qui est plus précis que le nôtre. */
/** Dernier recours seulement : au-dessus des 250 s du proxy, eux-mêmes
 *  au-dessus d'IA_TIMEOUT_MS du backend. Le moteur tourne sur CPU — plusieurs
 *  minutes par trame — et l'échéance qui doit s'exprimer est celle du backend,
 *  qui répond proprement ; celle-ci ne tire que si la chaîne entière est morte. */
const DELAI_IA_CLIENT_MS = 270_000;

/** Contenu d'un fichier en base64, sans le préfixe `data:` du FileReader. */
function enBase64(f: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onerror = () => reject(new Error('Lecture du fichier impossible.'));
    r.onload = () => resolve(String(r.result || '').split(',')[1] || '');
    r.readAsDataURL(f);
  });
}

/** Métadonnées d'une pièce du dossier d'équipement — indépendantes du fichier,
 *  qui peut ne pas exister (notice restée sur le portail du fabricant). */
/** Un point de règle tel que le moteur le sert (`GET /ged/regles`).
 *  ⚠️ Miroir de FORME seulement : aucune valeur n'est recopiée ici — elles sont
 *  dérivées côté moteur par `backend/src/regles-nc.ts`. */
export interface RegleServie {
  ref: string;
  version: number;
  ancre: string;
  titre: string;
  /** Ce qu'il faut remplir, en une ou deux phrases — ouvert par défaut ;
   *  la règle complète se déplie derrière (08/10/2026). */
  consigne?: string;
  regle: string[];
  valeurs: { lu: true; texte: string }[];
  norme: { article?: string; nImposePas?: string };
  pourquoi: string;
  depuis: { par: string; le: string; applicableLe?: string };
  /** Calculé PAR LE MOTEUR (REG-3) : `a_revalider` dit que la règle a changé
   *  dans le logiciel depuis que cet énoncé a été approuvé. L'écran ne le
   *  recalcule pas — il lui faudrait une copie du mécanisme d'empreinte. */
  etat?: 'applicable' | 'a_revalider';
}

export interface PieceMeta {
  nature: string; libelle: string;
  reference?: string; version?: string; url?: string; dateDocument?: string;
  /** Jusqu'à quand la pièce ENGAGE (lot ISO-5). Sur un certificat
   *  d'étalonnage, c'est elle — et non la date d'émission — qui dit si le
   *  raccordement tient encore. Vide = durée d'engagement inconnue, ce qui
   *  n'est pas « valable indéfiniment ». */
  dateValidite?: string;
}

/** Ce qu'un fichier déclare de lui-même, rendu par `POST /ged/equip/piece/analyser`.
 *
 *  ⚠️ `trouve` est la partie qui compte à l'écran. Elle NOMME les champs
 *  réellement lus dans le fichier : sans elle, un formulaire pré-rempli et un
 *  formulaire vide se ressemblent, et l'utilisateur ne sait pas s'il relit une
 *  donnée du document ou s'il en invente une. `declare` porte le bloc brut —
 *  titre, auteur, sujet, mots clés — MONTRÉ et jamais mappé : un PDF n'a pas de
 *  case « référence », et en fabriquer une par ressemblance donnerait une
 *  référence d'apparence lue. */
export interface PieceAnalysee {
  ok: boolean;
  ext: string;
  taille: number;
  titre: string;
  dateDocument: string;
  declare: { cle: string; valeur: string }[];
  trouve: string[];
  /** Phrase à afficher telle quelle quand rien n'a pu être lu. Vide sinon. */
  empeche: string;
}

export interface EngagementLigne { code: string; titre: string; statut: string }
export interface Engagements {
  key: string; total: number;
  redaction: EngagementLigne[]; verification: EngagementLigne[]; approbation: EngagementLigne[];
  accuses: EngagementLigne[]; pilotage: EngagementLigne[]; smq: EngagementLigne[];
  /** Habilitations détenues — EN LECTURE SEULE : une compétence ne se transfère
   *  pas d'un clic, et l'effacer réécrirait le passé. Elles comptent dans le
   *  total pour que le départ DISE la couverture qu'il emporte (12/10/2026).
   *  Optionnel : un moteur antérieur à ce jour-là ne les sert pas. */
  habilitations?: EngagementLigne[];
}
export interface TransfertPayload {
  redaction?: string[]; verification?: string; approbation?: string;
  pilotage?: string; smq?: string;
  accuses?: 'retirer' | 'transferer'; accusesVers?: string[];
}

/**
 * ÉTAT DU MOTEUR D'ASSISTANCE — trois situations, trois mots (lot GED-13).
 *
 * L'écran les confondait toutes sous un `iaDisponible` faux, alimenté aussi
 * bien par « aucun serveur n'est configuré » que par « la requête d'état a
 * échoué ». Or ce ne sont pas les mêmes faits, et l'utilisateur n'en tire pas
 * la même conclusion :
 *  · `eteint`      — le laboratoire n'a pas encore de serveur d'inférence. Rien
 *                    n'est cassé, il n'y a rien à réessayer, et c'est l'état
 *                    NOMINAL tant que les serveurs ne sont pas achetés.
 *  · `injoignable` — le serveur devrait répondre et ne répond pas : il y a
 *                    quelque chose à signaler à l'informatique.
 *  · `pret`        — l'assistance est utilisable.
 *  · `attente`     — l'état n'a pas encore été demandé (premier rendu).
 */
export type EtatMoteurIA = 'attente' | 'pret' | 'eteint' | 'injoignable';

/** Pourquoi une demande d'assistance n'a rien rendu. Même exigence qu'au-dessus :
 *  une PANNE et un DÉLAI DÉPASSÉ ne se traitent pas pareil — le premier se
 *  signale, le second se réessaie sur un texte plus court. */
export type EchecIA = 'eteint' | 'panne' | 'delai' | 'refus' | 'droit';

export type ResultatIA =
  | { ok: true; suggestion: Record<string, unknown> }
  | { ok: false; motif: EchecIA; message: string };

/** Les sélections de fiche que porte le magasin — une par registre. */
/** Réexportée : elle est DÉCLARÉE dans `types.ts`, la feuille sans import.
 *  Les nombreux appelants qui l'importent d'ici continuent de fonctionner. */
export type { CleSelection } from '@/lib/types';

/** Une étape du chemin : l'écran quitté, ce qui y était ouvert, et son nom. */
/**
 * Où l'on LISAIT, et pas seulement quel écran (14/08/2026).
 *
 * `window.scrollY` ne suffit plus depuis que les écrans maître-détail donnent
 * à chaque colonne sa propre zone de défilement : sur la liste des
 * équipements, la page ne bouge PAS — c'est la colonne de gauche qui défile.
 * Revenir ne rendait donc jamais l'endroit d'où l'on avait cliqué, et il
 * fallait rechercher sa ligne dans 237 fiches.
 *
 * On relève donc aussi les zones internes. La clé est POSITIONNELLE
 * (`col:0`, `col:1`, `nav`) : elle survit à un remontage des composants, là
 * où une référence d'élément serait morte au retour.
 */
export interface ScrollInterne { cle: string; top: number }

export function capturerScrolls(): ScrollInterne[] {
  if (typeof document === 'undefined') return [];
  const out: ScrollInterne[] = [];
  const nav = document.querySelector('.ged-nav-colonne');
  if (nav && nav.scrollTop > 0) out.push({ cle: 'nav', top: nav.scrollTop });
  const grille = document.querySelector('.ged-colonnes-defilantes');
  if (grille) {
    [...grille.children].forEach((c, i) => {
      const t = (c as HTMLElement).scrollTop;
      if (t > 0) out.push({ cle: `col:${i}`, top: t });
    });
  }
  return out;
}

/** Repose ce que `capturerScrolls` avait relevé. Silencieux si la zone n'existe
 *  plus : un écran peut avoir changé de forme entre-temps. */
export function reposerScrolls(scrolls: ScrollInterne[]): void {
  if (typeof document === 'undefined') return;
  for (const s of scrolls) {
    if (s.cle === 'nav') {
      const nav = document.querySelector('.ged-nav-colonne');
      if (nav) nav.scrollTop = s.top;
      continue;
    }
    const m = /^col:(\d+)$/.exec(s.cle);
    if (!m) continue;
    const grille = document.querySelector('.ged-colonnes-defilantes');
    const col = grille?.children[Number(m[1])] as HTMLElement | undefined;
    if (col) col.scrollTop = s.top;
  }
}

export interface EtapeRetour {
  view: GedView;
  /** Toutes les sélections au moment du départ — restaurées telles quelles. */
  selection: Partial<Record<CleSelection, string | null>>;
  /** Ce qu'on lit dans le fil d'Ariane : « EQ08 », « BIO24-NC-2026-036 ». */
  libelle: string;
  /** Le filtre de registre en cours au départ. Il était perdu au retour : on
   *  revenait sur la liste COMPLÈTE là où l'on avait laissé « les miennes ». */
  smqFiltre: string | null;
  /** Position de lecture. Sans elle, le retour reposait en haut d'un registre
   *  de 136 lignes qu'on venait de parcourir jusqu'au bout. */
  scrollY: number;
  /** Position DANS les colonnes défilantes — voir `capturerScrolls`. */
  scrollsInternes: ScrollInterne[];
  /** Onglet ouvert dans la fiche quittée (`onglets` ci-dessous). */
  onglets: Record<string, string>;
}

/**
 * Fiche liée ouverte EN PANNEAU, par-dessus l'écran courant (12/08/2026).
 *
 * `type` désigne la nature de l'objet, `valeur` son identifiant (documentId, ou
 * code pour un document), `origine` ce qu'on lit dans l'en-tête du panneau —
 * « ouvert depuis BIO24-NC-2026-036, qui reste ouvert dessous ».
 */
export type TypePanneau = 'nc' | 'action' | 'doc' | 'equip' | 'recl' | 'audit' | 'maintenance'
  /* 18/08/2026 — le PARAMÈTRE du catalogue. Il manquait, et c'est ce qui
     rendait le référentiel des critères de performance inerte : ses lignes
     nomment des paramètres, et rien ne permettait de les ouvrir sans quitter
     l'écran de comparaison qu'on est en train de lire. `valeur` porte le
     documentId OU le code — la route accepte les deux. */
  | 'parametre';
export interface CiblePanneau {
  type: TypePanneau;
  valeur: string;
  origine: string;
  /** Document : onglet à ouvrir. Une tâche « à rédiger » vise le fichier. */
  onglet?: GedState['docTab'];
  /** Document : montrer la version EN CHANTIER plutôt que l'applicable — réservé
   *  aux tâches « à rédiger / à vérifier / à approuver » (RQ, 06/10/2026). */
  voirEnCours?: boolean;
}

interface GedState {
  session: GedSession;
  booted: boolean;
  /** L'écran demande d'abord si l'assistance est disponible : sans clé API
   *  côté serveur, il masque ses boutons au lieu de proposer une aide qui
   *  échouera. Un bouton qui ne marche pas est pire que pas de bouton. */
  iaDisponible: boolean;
  /** Le même fait, mais DISTINGUÉ (GED-13) : « pas de serveur » et « serveur
   *  injoignable » se lisent tous deux `iaDisponible: false`, et un écran qui
   *  doit expliquer la situation à l'utilisateur a besoin de savoir laquelle. */
  iaEtat: EtatMoteurIA;
  /** Tâches que MON rôle peut invoquer (le serveur filtre déjà). */
  iaTaches: string[];
  /** L'entretien de déclaration NC (Claude, 19/10/2026) est-il configuré ?
   *  Indépendant du moteur local : l'un peut répondre sans l'autre. */
  iaEntretienNC: boolean;
  /** Le catalogue des points de règle, servi par le moteur (REG-1).
   *  Vide tant qu'il n'a pas répondu : un « i » sans règle ne s'affiche pas,
   *  donc l'écran ne montre jamais un marqueur qui n'ouvrirait rien. */
  regles: RegleServie[];
  view: GedView;
  // Le paramétrage Qualité est une FENÊTRE FLOTTANTE (comme le centre de
  // paramétrage du manuel), pas une vue : il se superpose à l'écran courant,
  // qui reste en place derrière.
  settingsOpen: boolean;
  docs: GedDocument[];
  demandes: GedDemande[];
  settings: GedSettings | null;
  events: Record<string, GedEvent[]>;   // par docCode / ref SMQ
  ncs: GedNC[];
  actions: GedAction[];
  recls: GedReclamation[];
  audits: GedAudit[];
  rdds: GedRdd[];
  equips: GedEquipement[];
  /** « Inclure les objets de recette » — préférence d'AFFICHAGE, hors prod only. */
  inclureRecette: boolean;
  setInclureRecette: (v: boolean) => void;
  fourns: GedFournisseur[];
  lots: GedLot[];
  cqis: GedCqi[];
  /** Cartes CQI du modèle C2/C3 — `cqis` est vide depuis la reprise. */
  cartesCqi: GedCqiCarteResume[];
  /** Récapitulatif des cartes CQI, SERVI calculé par le moteur — jamais
   *  recompté à l'écran : deux endroits qui comptent, ce sont deux occasions
   *  de diverger. */
  recapCqi: RecapCqi | null;
  eeqs: GedEeq[];
  analytes: GedAnalyte[];
  liens: GedLien[];
  copies: GedCopie[];
  /** Plan de maintenance préventive + occurrences PROJETÉES (lot N4).
   *  `null` tant qu'il n'a pas été chargé : distinguer « pas encore lu » de
   *  « registre vide » évite d'annoncer « aucune maintenance planifiée » à
   *  quelqu'un dont la requête a simplement échoué. */
  maintenances: MaintenanceRegistre | null;
  selectedNC: string | null;            // documentId
  selectedAction: string | null;
  selectedRecl: string | null;
  selectedAudit: string | null;
  selectedRdd: string | null;
  selectedEquip: string | null;
  /** RÉFÉRENCE à ouvrir sur l'écran vers lequel on vient de naviguer.
   *
   *  Point de rendez-vous entre l'URL (`?view=…&ref=…`, posée par le centre
   *  des tâches) et l'écran d'arrivée, qui seul sait ce que sa référence
   *  désigne — une occurrence de maintenance, une carte, une ligne de plan.
   *  L'écran la CONSOMME : il l'ouvre puis la remet à `null`, sans quoi
   *  revenir sur l'écran plus tard rouvrirait la même pièce sans raison. */
  refCible: string | null;
  setRefCible: (v: string | null) => void;
  selectedFourn: string | null;
  selectedCode: string | null;
  /** Cf. `CleSelection` : « <liste>:<code> » du référentiel des analyses. */
  selectedRef: string | null;
  /** Code du poste dont la FICHE est ouverte (19/08/2026). */
  selectedPoste: string | null;
  /** Fiche de l'annuaire ouverte, par sa référence (`ORG-0163`, `PER-0284`). */
  selectedAnnuaire: string | null;
  /** Convention ouverte, par sa référence (`CONV-0004`). */
  selectedConvention: string | null;
  /** Le poste que l'écran de composition doit ouvrir d'emblée, posé par la
   *  fiche de poste — sans quoi « Composer les tâches » retomberait sur un
   *  sélecteur vide et l'on croirait le poste sans composition. */
  /** C8 — la carte CQI à ouvrir en arrivant sur l'écran, par sa RÉFÉRENCE
   *  `EQUIPEMENT:ANALYTE` (jamais un documentId : l'adresse doit survivre à une
   *  reprise de base, cf. la doctrine d'adressage). */
  selectedCqiCarte: string | null;
  /** `orgId` de la personne dont le DOSSIER d'habilitation est ouvert
   *  (25/08/2026) — cf. `CleSelection`. */
  selectedPersonne: string | null;
  docTab: 'lecture' | 'fichier' | 'versions' | 'journal' | 'diffusion' | 'comments' | 'liens';
  /* ── LA FICHE MONTRE LA VERSION APPLICABLE, SAUF SI ON VIENT TRAVAILLER ───
     RQ, 06/10/2026 : « au premier abord je voudrais voir la version en cours
     [applicable] avec une mention qu'une nouvelle version est en cours… SAUF
     dans le cas où j'y accède depuis le menu "à vérifier", "à rédiger" ou
     "à approuver" ». Ce drapeau porte exactement cette exception : il est posé
     par les tâches (`openDoc(..., true)`, `ouvrirPanneau({ voirEnCours: true })`)
     et par le lien « consulter la nouvelle version » de la fiche. Il retombe à
     chaque ouverture ordinaire d'un document. */
  docVoirEnCours: boolean;
  /* ── Ouverture par LIEN partagé (14/08/2026) ─────────────────────────────
     `lienEnAttente` : une adresse a désigné une fiche par sa RÉFÉRENCE, et le
     registre qui permet de la retrouver n'est pas encore arrivé. Cet état-là
     n'est ni « ouvert » ni « introuvable » — les confondre afficherait « cette
     fiche n'existe pas » pendant la demi-seconde de chargement, et suffirait à
     faire perdre confiance dans TOUS les liens envoyés.

     `erreurLien` : la référence a été cherchée dans un registre CHARGÉ et ne
     s'y trouve pas. Le destinataire doit le lire, pas être reposé en silence
     sur le tableau de bord. */
  lienEnAttente: { vue: GedView; reference: string } | null;
  erreurLien: string | null;
  fQuery: string; fType: string; fDisc: string; fStatut: string;
  /** Filtre par PROCESSUS du catalogue documentaire — axe distinct de `fDisc`
   *  (famille biologique) : les deux se croisent au lieu de s'exclure. */
  fProc: string;
  /** « Mes documents » (04/10/2026) : '' · 'amoi' · 'amoi:<etape>' · 'implique' — voir `lib/mes-documents.ts`. */
  fMoi: string;
  /** Filtre à appliquer À L'ARRIVÉE sur le prochain registre SMQ ouvert (NC,
   *  actions, réclamations, audits). Les registres gardent leur filtre en état
   *  local ; ce champ n'est lu qu'au montage de l'écran, et `go` le remet à
   *  `null` pour qu'une navigation ordinaire n'hérite jamais d'un filtre posé
   *  par un compteur cliqué plus tôt. */
  smqFiltre: string | null;
  toast: string | null;
  /** Sévérité du message courant : une erreur ne doit ni ressembler à une
   *  confirmation ni s'effacer toute seule (QA v2 UX-01/UX-03).
   *
   *  `indetermine` est un TROISIÈME cas, ajouté le 08/08/2026 : l'écriture est
   *  partie sans réponse. Ce n'est pas un refus — il n'y a rien à corriger — et
   *  ce n'est pas un succès. La seule conduite utile est d'aller voir, pas de
   *  recliquer : le toast propose donc d'actualiser, jamais de refaire. */
  toastSeverite: 'info' | 'erreur' | 'indetermine';
  /** Le geste que propose le toast, s'il y en a un : refaire l'appel refusé,
   *  ou relire le registre après une écriture indéterminée. */
  toastAction: { label: string; faire: () => void } | null;
  /** Confirmation demandée par une ACTION DU MAGASIN (qui ne peut pas porter `useConfirmation`) :
   *  le Shell l'affiche dans la fenêtre commune. Remplace `window.confirm` (UI-CONVENTIONS §4). */
  confirmationGlobale: { question: string; titre: string; confirmer: string; danger: boolean; resoudre: (ok: boolean) => void } | null;
  demanderConfirmation: (question: string, opts?: { titre?: string; confirmer?: string; danger?: boolean }) => Promise<boolean>;
  repondreConfirmation: (ok: boolean) => void;
  loading: boolean;
  /** `true` quand le dernier `refresh` n'a pas pu joindre le backend. Sert à
   *  distinguer un écran RÉELLEMENT vide d'un écran vide PARCE QUE le serveur
   *  n'a pas répondu — un RQ ne doit pas conclure à une perte de données sur un
   *  simple redémarrage (constat QA majeur du 30/07). */
  backendError: boolean;

  /**
   * Le CHEMIN parcouru de fiche en fiche — pas l'historique du navigateur.
   *
   * Ouvrir la non-conformité d'un équipement, puis l'action qui en découle,
   * fait traverser trois registres : sans mémoire, fermer la dernière fiche
   * laissait sur le registre des actions, à mille lieues de l'équipement d'où
   * l'on venait (constat de recette A4, 08/08/2026). Chaque étape retient d'où
   * elle a été ouverte, ce qui y était sélectionné, et sous quel nom le dire.
   *
   * Une navigation ORDINAIRE (barre latérale, `go`) la vide : on n'est plus en
   * train de suivre un fil, on a changé de sujet.
   */
  pileRetour: EtapeRetour[];
  /** Position de lecture à reposer APRÈS le rendu de l'écran retrouvé, ou
   *  `null` quand il n'y a rien à restaurer (navigation ordinaire → haut). */
  scrollARestaurer: number | null;
  /** Zones internes à reposer en même temps que la page. */
  scrollsInternesARestaurer: ScrollInterne[];
  scrollRestaure: () => void;
  /** Navigation ORDINAIRE vers un écran, en désignant l'objet à y ouvrir.
   *  Remplace le couple `select` + `go`, dont l'ordre faussait l'historique. */
  naviguerVers: (v: GedView, cible?: { cle?: CleSelection; valeur?: string | null }) => void;
  /** Applique une adresse à l'état — au premier rendu et à chaque `popstate`. */
  appliquerAdresse: (chemin: string) => void;
  /** Écrans traversés, pour la flèche « revenir ». Voir le commentaire de
   *  `poserHistorique` : ce n'est PAS le fil d'Ariane. */
  historique: EtapeRetour[];
  /** Écrans quittés PAR un recul, dans l'ordre : de quoi « avancer » à nouveau,
   *  comme le bouton suivant d'un navigateur. Vidé dès qu'on repart ailleurs —
   *  un avenir qu'on n'a pas emprunté n'existe plus. */
  avenir: EtapeRetour[];
  /** Mémorise l'endroit COURANT avant de le quitter. Appelé par `go`,
   *  `goFiltre`, `ouvrirDepuis`, `openDoc` — le point de passage unique. */
  poserHistorique: () => void;
  reculer: () => void;
  avancer: () => void;
  /** Pile des fiches ouvertes en panneau. Empilée, pas remplacée : un lien
   *  cliqué DANS un panneau ouvre le suivant par-dessus, et « Retour » redonne
   *  le précédent — l'écran de départ, lui, n'a jamais bougé. */
  pilePanneaux: CiblePanneau[];
  ouvrirPanneau: (cible: CiblePanneau) => void;
  fermerPanneau: () => void;
  /** Ferme TOUS les panneaux d'un coup (Échap sur le premier, clic sur le fond). */
  fermerTousPanneaux: () => void;
  /** Le panneau courant passe en pleine page : pour le travail de fond, quand
   *  la fiche mérite tout l'écran. Le chemin de retour est alors alimenté. */
  panneauEnPleinePage: () => void;
  /** Onglet courant de chaque fiche, par clé d'écran (`nc`, `aud`…). Remonté
   *  dans le magasin pour survivre au démontage — sans quoi le retour rouvrait
   *  toujours le premier onglet. */
  onglets: Record<string, string>;
  setOnglet: (cle: string, valeur: string) => void;

  boot: () => Promise<void>;
  refresh: () => Promise<void>;
  go: (v: GedView) => void;
  /** Le menu du profil demande un congé sans passer par l'écran : il pose le
   *  drapeau, « Mes congés » l'ouvre puis le consomme. Un booléen plutôt qu'un
   *  appel direct — le formulaire vit dans l'écran, pas dans l'en-tête. */
  /** Ouvre une fiche DEPUIS une autre, en gardant le chemin pour y revenir.
   *  `origine` est ce qu'on lira dans le fil d'Ariane (« EQ08 »). */
  ouvrirDepuis: (origine: string, cible: { view: GedView; cle?: CleSelection; valeur?: string | null }) => void;
  /** Revient à l'étape précédente du chemin, sélection comprise. */
  retourPile: () => void;
  /** Ouvre un registre SMQ avec un filtre déjà appliqué : un compteur désigne
   *  un sous-ensemble, il doit y mener — pas sur une liste complète. */
  goFiltre: (v: GedView, filtre: string) => void;
  /** L'onglet demandé à l'ouverture. `null` = le premier auquel on a droit.
   *  Il vit ICI et non dans l'écran : la recherche générale doit pouvoir
   *  ouvrir le paramétrage SUR un onglet, et un état local ne se pilote pas
   *  du dehors. */
  settingsTab: string | null;
  openSettings: (tab?: string) => void;
  closeSettings: () => void;
  openDoc: (code: string, tab?: GedState['docTab'], voirEnCours?: boolean) => void;
  setDocTab: (t: GedState['docTab']) => void;
  setDocVoirEnCours: (v: boolean) => void;
  setFilter: (k: 'fQuery' | 'fType' | 'fDisc' | 'fProc' | 'fStatut' | 'fMoi', v: string) => void;
  /** `heritage` : une ANCIENNE clé de chaînage (code de classeur papier) dont
   *  l'historique se lit encore — fusionné AVANT la clé courante, jamais écrit
   *  (décision du 24/08/2026 : le registre, c'est l'appli, plus le classeur). */
  loadJournal: (docCode: string, heritage?: string) => Promise<void>;
  /** Charge le plan de maintenance et ses occurrences projetées (lot N4).
   *  La fenêtre est facultative : par défaut le mois précédent, le mois courant
   *  et les deux suivants — assez pour le calendrier du tableau de bord et pour
   *  ne pas faire disparaître une échéance non faite le 1er du mois. */
  loadMaintenances: (du?: string, au?: string) => Promise<void>;
  showToast: (m: string, severite?: 'info' | 'erreur' | 'indetermine', action?: { label: string; faire: () => void }) => void;
  dismissToast: () => void;

  // moteur — chaque action rafraîchit le document et son journal
  act: (docId: string, action: string, body?: unknown) => Promise<boolean>;
  createDemande: (description: string, propositions: unknown) => Promise<boolean>;
  decideDemande: (id: string, decision: string, motif?: string, assign?: {
    redacteur?: string; redacteurs?: string[]; verificateur?: string; approbateur?: string; periodicite?: string;
    /** Corrections des métadonnées au moment d'approuver (14/08/2026). Une clé
     *  absente laisse la proposition du demandeur intacte. */
    meta?: Record<string, unknown>;
  }) => Promise<boolean>;
  addDemandeNote: (id: string, texte: string) => Promise<boolean>;
  reopenDemande: (id: string) => Promise<boolean>;
  // Départ / absence longue : inventaire puis transfert des responsabilités.
  loadEngagements: (key: string) => Promise<Engagements | null>;
  transferer: (key: string, payload: TransfertPayload) => Promise<boolean>;
  /** `versions` : les empreintes des réglages TELLES QUE L'ÉCRAN LES A LUES
   *  (`settings.versions` au moment où sa copie a été prise). Sans elles, le
   *  serveur ne peut pas voir qu'un autre a écrit entre-temps. */
  saveSettings: (patch: Partial<GedSettings>, versions?: Record<string, string>) => Promise<boolean>;
  // SMQ — POST générique vers le moteur (ged/nc, ged/actions, ged/recl…) + refresh
  /** `contexte` nomme l'action au présent — « Enregistrement de la
   *  réclamation ». Il ne sert QU'EN CAS D'ÉCHEC : le message du moteur dit
   *  pourquoi, le contexte dit de quoi il parle. Facultatif : sans lui, le
   *  message du moteur s'affiche seul, comme avant. */
  /** Nombre d'écritures EN VOL. Un compteur et non un booléen : deux actions
   *  lancées coup sur coup, et la première qui finit éteindrait le témoin
   *  alors que la seconde travaille encore. */
  ecrituresEnCours: number;
  /** `methode` : `POST` par défaut. `DELETE` porte un corps — le proxy le
   *  transmet depuis le 14/08 (piège nº1 : il le jetait). */
  smq: (path: string, body?: unknown, okToast?: string, contexte?: string, methode?: 'POST' | 'DELETE') => Promise<boolean>;
  /** Même chose, mais rend la réponse du moteur — nécessaire quand l'objet créé
   *  doit être ouvert dans la foulée (une action corrective, qui reste à
   *  remplir). `null` en cas d'échec : le toast d'erreur est déjà émis. */
  smqData: <T = Record<string, unknown>>(path: string, body?: unknown, okToast?: string, contexte?: string) => Promise<T | null>;
  /**
   * INTERROGATION : un POST qui ne modifie RIEN. Certaines questions ont besoin
   * d'un corps (une liste d'identifiants, par exemple) et ne peuvent donc pas
   * passer par un GET — l'aperçu d'une relance de masse en est une.
   *
   * ⚠️ Ce n'est pas `smqData` avec un autre nom. `smqData` rafraîchit le magasin
   * et allume le témoin « écriture en cours » : l'employer pour une lecture
   * ferait clignoter « enregistrement » alors que rien n'est enregistré, et
   * rejouerait tout le chargement pour rien.
   *
   * ⚠️ SANS `body`, L'APPEL EST UN GET. Beaucoup de routes portent le même
   * chemin en GET (lire) et en POST (créer) : poster sans corps taperait sur la
   * création et rendrait une liste vide — donc un écran qui AFFIRME « aucun »
   * sur une base pleine.
   */
  interroger: <T = Record<string, unknown>>(path: string, body?: unknown) => Promise<T | null>;
  /** Signale au moteur qu'on vient d'OUVRIR un dossier NC ou réclamation.
   *  Voir l'implémentation : silencieuse par construction. */
  signalerLecture: (registre: 'nc' | 'recl', documentId: string) => Promise<void>;
  /** Audience de diffusion (lot N8 bis) — LECTURE de la règle et de ce qu'elle
   *  désigne AUJOURD'HUI. La résolution est faite par le serveur : l'écran ne
   *  la refait pas, il l'affiche. `null` en cas d'échec, toast déjà émis. */
  lireAudience: (docId: string) => Promise<AudienceApercu | null>;
  /** QUI une règle NON ENREGISTRÉE désignerait aujourd'hui — l'aperçu en
   *  direct de la fenêtre de diffusion (04/10/2026). Lecture pure, calculée par
   *  le serveur ; silencieuse en cas d'échec (elle se rejoue au clic suivant). */
  apercuAudienceBrouillon: (docId: string, audience: AudienceRegle) => Promise<Pick<AudienceApercu, 'designes' | 'ecartes' | 'steriles' | 'nouveaux'> & { erreur: string } | null>;
  /** Enregistre la règle d'audience (ou la retire avec `null`). Elle se règle
   *  jusqu'à la diffusion comprise ; figée sur un document obsolète ou retiré. */
  enregistrerAudience: (docId: string, audience: AudienceRegle | null) => Promise<boolean>;
  /** Assistance IA (catalogue de tâches côté serveur) — elle PROPOSE, elle
   *  n'écrit jamais : pas de `refresh()` ici, contrairement à `smq`/`smqData`.
   *
   *  Le résultat est un union DISCRIMINÉ (GED-13) et non plus `… | null` :
   *  l'écran doit pouvoir dire « le moteur n'est pas installé », « il n'a pas
   *  répondu » et « il a mis trop de temps » sans les confondre, ce qu'un
   *  `null` unique rendait impossible.
   *
   *  `silencieux` supprime le toast, pour les écrans qui affichent l'échec
   *  EN PLACE — un toast qui répète un bandeau déjà visible fait du bruit. */
  assisterIA: (
    tache: string, entree: string, contexte?: Record<string, unknown>,
    options?: { silencieux?: boolean },
  ) => Promise<ResultatIA>;
  /** Un tour de l'entretien de déclaration NC (Claude, 19/10/2026). N'écrit
   *  rien : rend des questions ou une fiche que le formulaire reprend. L'échec
   *  rend sa phrase, affichée EN PLACE par l'écran (pas de toast). */
  entretienNC: (
    echanges: { role: 'declarant' | 'assistant'; texte: string }[], conclure: boolean,
  ) => Promise<{ ok: true; reponse: Record<string, unknown> } | { ok: false; message: string }>;
  /** Interrogé au démarrage (comme `loadRhAcces`) : sans clé API côté serveur,
   *  `iaDisponible` reste faux et les écrans masquent leurs boutons d'assistance. */
  chargerIA: () => Promise<void>;
  chargerRegles: () => Promise<void>;
  creerLien: (l: { sourceType: string; sourceRef: string; cibleType: string; cibleRef: string; typeLien: string }) => Promise<boolean>;
  retirerLien: (documentId: string) => Promise<boolean>;
  select: (k: CleSelection, id: string | null) => void;
  /** Joint un fichier à une intervention, ou au dossier de l'équipement. */
  joindreFichierEquip: (
    equipId: string,
    cible: { type: 'intervention'; id: string } | ({ type: 'piece' } & PieceMeta),
    fichier: File,
  ) => Promise<boolean>;
  /** Enregistre une pièce SANS fichier : la référence du document officiel et
   *  où le trouver. Cas courant des notices constructeur. */
  enregistrerReference: (equipId: string, meta: PieceMeta) => Promise<boolean>;
  /** CE QUE LE FICHIER DÉCLARE DE LUI-MÊME, avant tout dépôt.
   *
   *  Lecture pure : le serveur lit les métadonnées du PDF (titre, date) ou
   *  l'EXIF d'une photo, et n'écrit rien. Le dépôt reste un second geste — ce
   *  qui laisse le temps de corriger ce qui a été lu, et d'écrire ce qui
   *  manque. Rend `null` si l'analyse a échoué : l'écran le DIT et attend la
   *  saisie plutôt que de laisser croire qu'il n'y avait rien à trouver. */
  analyserPieceEquip: (fichier: File) => Promise<PieceAnalysee | null>;
  retirerPieceEquip: (equipId: string, pieceId: string) => Promise<boolean>;
  etiquetteEquip: (equipId: string) => Promise<GedEtiquette | null>;
  /** Aperçu SEUL — jamais d'écriture — de ce qu'une sortie de service (ou une
   *  panne) emporterait sur les techniques et les examens. Appelée à
   *  l'ouverture des deux formulaires concernés (`SortieDuService`,
   *  déclaration de panne) pour que l'avertissement soit visible AVANT la
   *  confirmation, jamais après. */
  impactSortieEquip: (equipId: string) => Promise<ImpactSortieEquip | null>;

  // ---- Module RH (lot 1) ----
  // Chargé À PART du refresh général : les données RH ne concernent qu'une
  // poignée de personnes, les tirer à chaque rafraîchissement de la Qualité
  // ferait payer à tout le monde un module dont ils n'ont pas l'accès.
  rhAcces: RhAcces | null;
  /** `true` si le dernier `loadRhAcces` a échoué (réseau/serveur) — distingue
   *  une panne d'un vrai refus d'accès, que `rhAcces === null` seul ne permet
   *  pas de savoir. */
  rhAccesErreur: boolean;
  /**
   * Nombre de lectures RH en vol. Un COMPTEUR, pas un booléen : au démarrage
   * quatre chargements se chevauchent, et le premier à finir remettrait un
   * booléen à faux pendant que les autres tournent encore — l'écran se dirait
   * prêt en affichant une liste vide, ce qui se lit « il n'y a rien ».
   */
  rhChargement: number;
  rhPersonnel: RhPersonnel[];
  rhPostes: RhPoste[];
  rhAffectations: RhAffectation[];
  /**
   * Le planning est-il MODIFIABLE par l'utilisateur courant (lot N6) ?
   *
   * Servi par le moteur avec les affectations, jamais déduit ici : c'est la
   * même réponse que celle qui gardera l'écriture. Une seconde règle écrite
   * dans l'interface aurait fini par proposer un bouton que le serveur refuse.
   */
  rhPlanningModifiable: boolean;
  /** États d'ouverture calculés par le serveur, un par poste et par jour. */
  rhOuvertures: RhOuverture[];
  rhModelesOuverture: RhModeleOuverture[];
  rhSemaine: string;                    // lundi de la semaine affichée (AAAA-MM-JJ)
  /** A3 — LA VUE MENSUELLE. Elle ne remplace pas la semaine, elle la surplombe :
   *  la semaine sert à PLANIFIER (on y affecte, on y déroge), le mois sert à
   *  RELIRE — repérer un trou de couverture, une personne trop chargée, une
   *  rotation qui ne tourne pas. Deux jeux de données distincts, et non un
   *  seul étendu, pour que changer de mois ne perde jamais la semaine en
   *  cours d'édition ni ne rejoue les écritures sur une plage plus large. */
  rhMois: string;                       // mois affiché (AAAA-MM)
  rhAffectationsMois: RhAffectation[];
  rhOuverturesMois: RhOuverture[];
  /** `null` = jamais chargé ; sinon le mois que porte `rhAffectationsMois`. */
  rhMoisCharge: string | null;
  loadRhAcces: () => Promise<void>;
  /** Suspendre · archiver · réactiver un dossier, depuis la fiche personnelle. */
  changerStatutPersonne: (orgId: string, statut: 'actif' | 'suspendu' | 'sorti', motif?: string) => Promise<boolean>;
  /** Contrats d'une personne — chargés à l'ouverture de sa fiche, pas au
   *  rafraîchissement général : ils ne concernent qu'elle. */
  chargerContrats: (orgId: string) => Promise<import('@/lib/types').RhContrat[]>;
  /** Enregistre un contrat. `documentId` absent = on met à jour le contrat
   *  ACTIF de la personne, ou on en crée un si elle n'en a pas — c'est ce que
   *  fait déjà `saveContrat` au serveur, et l'écran ne redécide pas. */
  enregistrerContrat: (c: Record<string, unknown>) => Promise<boolean>;
  /** Variante qui rend le `documentId` du contrat écrit (ou `null` si refusé) —
   *  nécessaire pour y joindre la pièce signée juste après sa création. */
  enregistrerContratId: (c: Record<string, unknown>) => Promise<string | null>;
  /** Pièce jointe d'un contrat (le contrat signé). Dépôt, retrait, ouverture. */
  joindrePieceContrat: (contratId: string, fichier: File) => Promise<boolean>;
  retirerPieceContrat: (contratId: string) => Promise<boolean>;
  ouvrirPieceContrat: (contratId: string, nom: string) => Promise<void>;
  /** Change l'IDENTIFIANT de connexion d'une personne (RQ ou RH). */
  changerIdentifiantCompte: (orgId: string, identifiant: string) => Promise<boolean>;
  loadRh: (lundi?: string) => Promise<void>;
  setRhSemaine: (lundi: string) => void;
  /** Charge le mois demandé (ou `rhMois`). Sans effet si ce mois est déjà en
   *  mémoire — l'aller-retour entre semaine et mois ne doit rien recharger. */
  loadRhMois: (mois?: string) => Promise<void>;
  /** Les affectations d'UN jour, en lecture seule — sert au post garde du 1er
   *  du mois, dont la garde (la veille) est hors du mois chargé. */
  lireAffectationsDuJour: (date: string) => Promise<RhAffectation[]>;
  setRhMois: (mois: string) => void;
  savePoste: (p: Partial<RhPoste>) => Promise<boolean>;
  retirerPoste: (code: string, motif: string) => Promise<boolean>;
  /** Rend le message de refus quand l'affectation est bloquée, `null` si elle
   *  est passée — l'écran a besoin du motif pour proposer la dérogation. */
  affecter: (a: { date: string; posteCode: string; personnelOrgId: string; heureDebut?: string; heureFin?: string;
    derogationMotif?: string;
    /** `formation` exige un tuteur — ce n'est PAS une dérogation, c'est le
     *  chemin ordinaire de l'apprentissage (PR-RH-001). */
    nature?: 'travail' | 'astreinte' | 'formation'; tuteurOrgId?: string;
    /** Un RQ qui n'a pas la main sur ce poste peut autoriser la dérogation
     *  depuis le poste de qui affecte, sans y ouvrir de session (06/09/2026,
     *  `autoriserResponsable` côté serveur). */
    responsable?: { identifiant: string; motDePasse: string } }) => Promise<string | null>;
  retirerAffectation: (documentId: string) => Promise<boolean>;
  /** Justifie un conflit d'affectation avec un congé — ne le fait PAS
   *  disparaître (06/09/2026, `derogerAbsence` côté serveur). Rend le message
   *  de refus, `null` si la dérogation est passée. */
  derogerAbsence: (affectationId: string, motif: string,
    responsable?: { identifiant: string; motDePasse: string }) => Promise<string | null>;

  // ---- Lot 2 : absences, congés, paramétrage ----
  // `mesAbsences` est chargé pour TOUT LE MONDE : déposer une demande de congé
  // ne dépend pas du rôle `rh`. Les autres listes, si.
  mesAbsences: RhAbsence[];
  rhAbsences: RhAbsence[];
  rhParametres: RhParametres | null;
  /** référentiel des habilitations (automates + activités) */
  rhHabilitations: RhHabilitation[];
  /** qui détient quoi, à quel niveau */
  rhDetenues: RhHabilitationDetenue[];
  /** contrats de tout l'effectif — le parcours d'intégration en a besoin */
  rhContrats: RhContrat[];
  /** dossiers d'intégration ouverts */
  rhIntegrations: RhIntegration[];
  /** les 4 phases de INS.G1.01, servies par le serveur */
  rhPhasesReference: RhIntegrationPhase[];
  /** Le bordereau tel que F-ENR-01 l'écrit — en regard de ce que chaque dossier
   *  a copié à son ouverture. L'écart entre les deux est l'information. */
  rhPochetteReference: { cle: string; libelle: string; origine: 'ged' | 'genere'; docCode?: string; reserve?: string }[];
  ouvrirIntegration: (orgId: string, dateDebut?: string) => Promise<boolean>;
  cocherActivite: (id: string, activiteId: string, fait: boolean, note?: string) => Promise<boolean>;
  remettrePiecePochette: (id: string, cle: string, remise: boolean) => Promise<boolean>;
  retenirHabilitations: (id: string, codes: string[]) => Promise<boolean>;
  cloturerIntegration: (id: string, conclusion: string, rouvrir?: boolean) => Promise<boolean>;
  /** ⚠️ `orgId` ET NON L'IDENTIFIANT DU PARCOURS (08/10/2026) : une pièce est
   *  rattachée à la PERSONNE. Avant, une personne sans parcours — toutes celles
   *  entrées avant 2026 — n'avait aucun endroit où classer sa CIN. */
  deposerPiece: (orgId: string, cle: string, libelle: string, f: File) => Promise<boolean>;
  /** Les pièces d'UNE personne, en métadonnées. `null` = lecture refusée ou en
   *  panne — jamais à confondre avec « aucune pièce » : un dossier qui
   *  s'affiche vide est une affirmation, pas une attente. */
  chargerPiecesPersonne: (orgId: string) => Promise<RhIntegrationPiece[] | null>;
  /** récupère le CONTENU d'une pièce — chaque appel est journalisé côté serveur */
  consulterPiece: (pieceId: string) => Promise<{ fichierNom: string; fichierData: string } | null>;
  supprimerPiece: (pieceId: string) => Promise<boolean>;
  saveHabilitation: (h: Partial<RhHabilitation>) => Promise<boolean>;
  /** Archive une habilitation (motif obligatoire, ≥ 20 caractères). Les
   *  attributions détenues sont PRÉSERVÉES. */
  archiverHabilitation: (id: string, motif: string) => Promise<boolean>;
  accorderHabilitation: (d: Partial<RhHabilitationDetenue>) => Promise<boolean>;
  retirerHabilitation: (id: string) => Promise<boolean>;
  /**
   * Arrivée d'une personne. Rend le SORT DU COMPTE et pas seulement un booléen
   * (GED-7) : l'invitation peut échouer — adresse déjà prise, identifiant déjà
   * porté — alors que la personne, elle, est bien créée. Rendre `true` ferait
   * croire que le compte est parti ; rendre `false` ferait croire que rien n'a
   * été enregistré. Les deux sont faux, et l'un des deux fait ressaisir.
   */
  creerPersonnel: (p: Record<string, unknown>) => Promise<{ ok: boolean; compte?: ResultatCompte } | null>;
  /** GED-7 — le profil que la fonction appellerait. `null` est une réponse. */
  profilPourFonction: (fonction: string) => Promise<PropositionCompte | null>;
  /** GED-8 — rapprochement comptes ↔ organigramme, dans les deux sens. */
  chargerRapprochementComptes: () => Promise<RapprochementComptes | null>;
  rhFeries: RhFerie[];
  /** Les jours chômés d'un mois `AAAA-MM` (dates ISO) — pour que la vue
   *  mensuelle compte les dimanches/jours chômés SANS porter elle-même la notion
   *  de férié (invariant de `qualite-planning-mois`). */
  joursChomesDuMois: (mois: string) => string[];
  /** Commentaire libre du planning d'un mois (RQ, 30/09/2026). */
  loadCommentairePlanning: (mois: string) => Promise<{ texte: string; parNom?: string; le?: string } | null>;
  enregistrerCommentairePlanning: (mois: string, texte: string) => Promise<{ ok: boolean; commentaire: { texte: string; parNom?: string; le?: string } | null }>;
  rhRegimes: RhRegime[];
  saveRegime: (r: Omit<RhRegime, 'documentId'>) => Promise<boolean>;
  supprimerRegime: (documentId: string) => Promise<boolean>;
  rhSoldes: RhSolde[];
  /** L'année lue par « Absences & congés » — gardée pour que les rechargements
   *  après une décision ne ramènent pas l'écran à l'année courante. */
  rhAnneeGestion: number | null;
  loadMesAbsences: () => Promise<void>;
  loadRhGestion: (annee?: number) => Promise<void>;
  demanderAbsence: (a: { motif: string; dateDebut: string; dateFin: string; demiJourneeDebut?: boolean; demiJourneeFin?: boolean; heureDebut?: string; heureFin?: string; personnelOrgId?: string; commentaire?: string; justificatifNom?: string; justificatifData?: string }) => Promise<boolean>;
  deciderAbsence: (id: string, decision: 'validee' | 'refusee' | 'annulee', motifDecision?: string, transfererAuRemplacant?: boolean) => Promise<boolean>;
  saveRhParametres: (p: Partial<RhParametres>, base?: Partial<RhParametres> | null) => Promise<boolean>;
  saveFerie: (f: { date: string; libelle: string; chome?: boolean }) => Promise<boolean>;
  supprimerFerie: (id: string) => Promise<boolean>;

  // ---- Lot 3 : présences ----
  rhJournees: RhJournee[];
  rapportImport: RapportImport | null;
  loadPresences: (lundi?: string) => Promise<void>;
  /* ── Pointage BRUT (GED-15 b) ────────────────────────────────────────────
   * Lecture, et RIEN d'autre. Il n'existe volontairement aucune action
   * d'écriture sur `rhPointages` : le brut est la référence opposable de la
   * paie, son immuabilité est ce qui lui donne sa valeur. Ajouter ici un
   * `corrigerPointage` reviendrait à retirer à la paie ce sur quoi elle
   * s'appuie. La correction se fait sur la JOURNÉE (`corrigerJournee`), avec
   * son motif, à côté du brut — jamais par-dessus. */
  rhPointages: RhPointage[];
  rhPointagesCharges: string | null;   // le lundi déjà chargé, pour ne pas relire
  loadPointages: (lundi?: string) => Promise<void>;
  /* ── Couverture des imports (26/08/2026) ─────────────────────────────────
   * CE QUI A ÉTÉ MESURÉ. Sans cette lecture, l'écran ne peut pas distinguer
   * « planifié, personne n'a badgé » de « planifié, mais aucun fichier n'a
   * jamais été importé » — et peignait le second en rouge, comme de
   * l'absentéisme. Lue une fois par ouverture de l'écran : une couverture ne
   * change qu'à l'import. */
  rhCouvertures: { du: string; au: string; lot?: string; fichier?: string; importePar?: string; importeLe?: string; note?: string; nbRetenus?: number; nbDoublons?: number; nbRejets?: number }[] | null;
  /** Annuler un import de pointage (RQ, 30/09/2026) — passages, période et journées effacés. */
  annulerImportPointage: (lot: string, motif: string) => Promise<boolean>;
  rhTrousImport: string[];
  loadCouvertures: () => Promise<void>;
  /** LE CONTRÔLE DE COHÉRENCE du mois affiché (29/09/2026) : congés contredits
   *  par le badge, personnes jamais lues, badges sans personne… Lu en direct —
   *  un constat disparaît dès que la donnée est corrigée. `null` = pas encore
   *  lu : on n'affiche RIEN plutôt qu'un « aucune incohérence » prématuré. */
  rhControle: { du: string; au: string; constats: ConstatPointage[]; passagesLus: number } | null;
  loadControle: (du: string, au: string) => Promise<void>;
  /** Appliquer une correction d'absence PROPOSÉE par le contrôle (29/09/2026) :
   *  ajuster ses dates, l'annuler, ou la confirmer (journées ramenées à 0 h).
   *  Toujours avec un motif — le solde ou la paie en dépendent. */
  appliquerPropositionAbsence: (c: ConstatPointage, p: PropositionAbsence, motif: string) => Promise<boolean>;
  /** Confirmer ou écarter un passage de badge (29/09/2026). Le brut n'est pas
   *  réécrit : la vérification s'ajoute à côté, motivée et signée. */
  verifierPointage: (id: string, statut: 'confirme' | 'ecarte' | 'aucune' | 'sens', motif: string, sens?: 'entree' | 'sortie', silencieux?: boolean) => Promise<boolean>;
  /** Les sens suspects du mois affiché (30/09/2026). `null` = pas encore lus. */
  rhSensSuspects: { du: string; au: string; suggestions: SensSuspect[] } | null;
  /** Le récapitulatif du mois par personne (30/09/2026). `null` = pas encore lu. */
  rhRecap: { mois: string; lignes: Record<string, number | string>[] } | null;
  loadRecapPresences: (mois: string) => Promise<void>;
  /** La fiche d'une personne pour le mois (30/09/2026) — lue à la demande. */
  loadDetailPresences: (mois: string, orgId: string) => Promise<Record<string, unknown> | null>;
  loadSensSuspects: (du: string, au: string) => Promise<void>;
  /* ── Journal RH ──────────────────────────────────────────────────────────
   * Les actes du module RH, lus par `GET ged/rh/journal` — la route GARDÉE
   * (`exigerRh` + rubrique « Journal RH »).
   *
   * ⚠️ Ne PAS remplacer par `loadJournal` : celui-ci lit `ged-events` par le
   * CRUD générique, qui n'a aucune de ces deux gardes. Il servirait
   * « justificatif consulté » ou « paie close » à qui voit la collection —
   * exactement ce que la rubrique existe pour fermer.
   *
   * `null` tant que rien n'a été lu : « journal vide » et « lecture refusée »
   * ne s'affichent pas de la même façon. */
  rhJournal: GedEvent[] | null;
  loadJournalRh: () => Promise<void>;
  /** `classeurB64` : le fichier BINAIRE d'un `.xlsx`/`.ods`, en base64. Le
   *  serveur le ramène au CSV que le lecteur de pointage sait déjà lire —
   *  un seul chemin d'analyse pour les trois formats. */
  importerPointages: (contenu: string, mappage?: Record<string, unknown>, classeurB64?: string) => Promise<boolean>;
  corrigerJournee: (id: string, heuresRetenues: number, motifEcart: string, reel?: { arrivee?: string; depart?: string }) => Promise<boolean>;
  /** Valider le temps en plus d'une journée en heures supplémentaires (30/09/2026). */
  validerHeuresSup: (id: string, heures: number, motif: string) => Promise<boolean>;
  /** Correction de MASSE : même motif pour toutes les journées sélectionnées.
   *  `mode` : 'planifiees' (retenir le prévu), 'pointees' (revenir au pointé),
   *  'heures' (valeur fixe — passer `heures`). */
  corrigerJourneesEnMasse: (documentIds: string[], mode: 'planifiees' | 'pointees' | 'heures', motif: string, heures?: number) => Promise<boolean>;
  validerPresences: (du: string, au: string, forcer?: boolean,
    responsable?: { identifiant: string; motDePasse: string },
    /** Une seule personne (fiche du 30/09/2026) — sinon tout le laboratoire. */
    personnelOrgId?: string) => Promise<boolean>;
  /** Les journées d'UNE personne sur une plage (fiche, 30/09/2026). */
  loadJourneesPersonne: (du: string, au: string, orgId: string) => Promise<RhJournee[]>;
  rouvrirJournee: (id: string) => Promise<boolean>;
  /** Reconstitue les journées d'une plage depuis pointages, planning et absences. */
  recalculerPresences: (du: string, au: string) => Promise<boolean>;
  majPersonnelRh: (orgId: string, champs: Record<string, unknown>) => Promise<boolean>;
  effacerRapportImport: () => void;

  // ---- Lot 4 : paie ----
  rhPaie: RhPaie | null;
  rhPeriode: string;                    // AAAA-MM
  setRhPeriode: (p: string) => void;
  loadPaie: (periode?: string) => Promise<void>;
  cloturerPaie: (periode: string, forcer?: boolean,
    responsable?: { identifiant: string; motDePasse: string }) => Promise<boolean>;
  exporterPaie: (periode: string) => Promise<boolean>;
  regulariserPaie: (r: { periode: string; personnelOrgId: string; libelle: string; quantite: number; unite: string }) => Promise<boolean>;
}

let toastTimer: ReturnType<typeof setTimeout> | null = null;

/**
 * Le message d'échec d'une écriture, et le geste qu'il propose.
 *
 * Trois décisions tiennent ici, et elles se lisent ensemble :
 *
 *  1. **Le contexte précède le motif.** « Service indisponible » seul ne disait
 *     pas quelle action avait échoué, et restait affiché longtemps après
 *     (recette A2). On lit désormais « Enregistrement de la réclamation —
 *     … » : le motif vient du moteur, le sujet vient de l'appelant.
 *
 *  2. **On ne propose « Réessayer » que si refaire est SANS RISQUE.** Un refus
 *     du moteur n'a rien écrit : recliquer est la bonne conduite. Une écriture
 *     indéterminée a peut-être abouti : proposer de refaire, c'est proposer un
 *     doublon — on propose de RELIRE le registre, ce qui répond à la seule
 *     question qui se pose alors (« est-ce passé ? »).
 *
 *  3. Le geste de reprise rejoue l'appel D'ORIGINE, pas une approximation :
 *     même chemin, même corps, même contexte.
 */
function signalerEchecEcriture(
  get: () => GedState,
  e: unknown,
  contexte: string | undefined,
  refaire: () => void,
) {
  const motif = e instanceof Error ? e.message : 'Action refusée';
  const indetermine = e instanceof ErreurAppel && e.indetermine;
  const message = contexte ? `${contexte} — ${motif}` : motif;
  get().showToast(
    message,
    indetermine ? 'indetermine' : 'erreur',
    indetermine
      ? { label: 'Actualiser', faire: () => { void get().refresh(); get().dismissToast(); } }
      : { label: 'Réessayer', faire: () => { get().dismissToast(); refaire(); } },
  );
}

/** Le fragment d'état qui éteint le message courant, minuterie comprise.
 *  Écrit une fois : quatre navigations l'appliquent, et une seule d'entre elles
 *  oubliant d'annuler la minuterie ferait réapparaître le vide 2,6 s plus tard. */
function sansToast() {
  if (toastTimer) { clearTimeout(toastTimer); toastTimer = null; }
  return { toast: null, toastAction: null } as const;
}

export const useGed = create<GedState>()((set, get) => ({
  session: { authenticated: false, key: '', name: '', profile: 'public', roles: [] },
  booted: false,
  iaDisponible: false,
  iaEtat: 'attente',
  iaTaches: [],
  iaEntretienNC: false,
  regles: [],
  view: 'dash',
  settingsOpen: false,
  settingsTab: null,
  ecrituresEnCours: 0,
  docs: [],
  demandes: [],
  settings: null,
  events: {},
  ncs: [],
  actions: [],
  recls: [],
  audits: [],
  rdds: [],
  equips: [],
  inclureRecette: false,
  fourns: [],
  lots: [],
  cqis: [],
  cartesCqi: [],
  recapCqi: null,
  eeqs: [],
  analytes: [],
  liens: [],
  copies: [],
  maintenances: null,
  selectedNC: null,
  selectedAction: null,
  selectedRecl: null,
  selectedAudit: null,
  selectedRdd: null,
  selectedEquip: null,
  refCible: null,
  setRefCible: (v) => set({ refCible: v }),
  selectedFourn: null,
  selectedCode: null,
  selectedRef: null,
  selectedPoste: null,
  selectedAnnuaire: null,
  selectedConvention: null,
  selectedCqiCarte: null,
  selectedPersonne: null,
  pileRetour: [],
  scrollARestaurer: null,
  scrollsInternesARestaurer: [],
  historique: [],
  avenir: [],
  docTab: 'lecture',
  docVoirEnCours: false,
  lienEnAttente: null, erreurLien: null,
  fQuery: '', fType: '', fDisc: '', fProc: '', fStatut: '', fMoi: '',
  smqFiltre: null,
  toast: null,
  toastSeverite: 'info',
  toastAction: null,
  confirmationGlobale: null,
  loading: false,
  backendError: false,

  boot: async () => {
    const session = await fetch('/api/auth/me', { credentials: 'include' }).then((r) => r.json()).catch(() => null);
    set({ session: session || get().session, booted: true });
    if (session?.authenticated) {
      await get().refresh();
      await get().chargerIA();
      await get().chargerRegles();
      // Le plan de maintenance alimente le CALENDRIER du tableau de bord : il
      // se charge au démarrage, comme le reste de ce que le calendrier agrège.
      // Séparé de `refresh()` parce que c'est une projection du moteur, pas une
      // collection REST — et parce que sa fenêtre est réglable.
      await get().loadMaintenances();
      // L'accès RH d'abord (il décide de l'affichage de la rubrique), puis les
      // données — `loadRh` ne demande rien si l'accès est refusé.
      await get().loadRhAcces();
      // Mes propres congés : pour TOUT LE MONDE. Déposer une demande ne dépend
      // pas du rôle `rh` — le nier ferait du self-service un privilège.
      await get().loadMesAbsences();
      await get().loadRh();
      await get().loadRhGestion();
      await get().loadPresences();
      await get().loadPaie();
    }
  },

  refresh: async () => {
    set({ loading: true });
    const [docs, demandes, settings, ncs, actions, recls, audits, rdds, equips, fourns, lots, cqis, eeqs, analytes, liens, cqiServi, copies] = await Promise.all([
      getAll('ged-documents?sort=code'),
      getAll('ged-demandes?sort=createdAt:desc'),
      api('ged-setting').then((d) => d?.data ?? null).catch(() => null),
      getAll('ged-ncs?sort=ref:desc'),
      getAll('ged-actions?sort=ref:desc'),
      getAll('ged-reclamations?sort=ref:desc'),
      getAll('ged-audits?sort=ref:asc'),
      getAll('ged-rdds?sort=ref:asc'),
      getAll('ged-equipements?sort=nom:asc'),
      getAll('ged-fournisseurs?sort=nom:asc'),
      getAll('ged-lots?sort=ref:desc'),
      getAll('ged-cqis?sort=analyte:asc'),
      getAll('ged-eeqs?sort=dateEeq:desc'),
      // Le référentiel des analytes : il sert au rattachement typé (« cette NC
      // concerne la ferritine »), qui n'était pas possible avant.
      getAll('ged-analytes?filters[actif][$eq]=true&sort=code:asc'),
      // Le registre des liens est servi par le MOTEUR, en UNE réponse. Le CRUD
      // REST le paginait par 100 : 1 135 liens = douze allers-retours, refaits
      // à chaque `refresh()`, donc après chaque écriture (recette A16).
      api('ged/liens').then((d) => d?.liens ?? []).catch(() => []),
      // Cartes CQI : servies par le moteur (elles agrègent période, points et
      // statut), pas par le CRUD REST qui ne renverrait que la coquille.
      /* ⚠️ ON GARDE AUSSI LE RÉCAPITULATIF, servi CALCULÉ par le moteur
         (relevé BLOQ-01, 26/08/2026). L'écran CQI le rechargeait par un appel
         qui lui était propre, en un seul coup : quand cet appel échouait, il
         affichait « Aucune carte de contrôle · 0 carte(s) » de façon
         DÉFINITIVE, pendant que la barre latérale — nourrie par ce magasin-ci —
         annonçait 41 cartes en défaut. Deux chemins de lecture pour la même
         donnée, et un seul qui se rejoue. Il n'y en a plus qu'un. */
      fetch('/api/ged/ged/cqi-cartes', { credentials: 'include' })
        .then((r) => (r.ok ? r.json() : { cartes: [], recapitulatif: null }))
        .then((d) => ({ cartes: d.cartes || [], recapitulatif: d.recapitulatif || null }))
        .catch(() => ({ cartes: [], recapitulatif: null })),
      // Registre des exemplaires papier : peu de lignes, mais elles décident
      // d'une tâche de terrain (aller remplacer une feuille affichée).
      getAll('ged-copies?sort=numero:asc'),
    ]);
    // `ged-setting` est un single-type toujours présent (seedé) : ne revenir
    // `null` que s'il n'a pas pu être chargé en fait un signal fiable de backend
    // injoignable. On distingue ainsi « écrans vides car serveur muet » de
    // « écrans réellement vides » (constat QA majeur du 30/07).
    /* ── LES OBJETS DE RECETTE SONT ÉCARTÉS ICI, UNE FOIS (BLOQ-04) ─────────
       Filtrer dans le magasin plutôt que dans chaque écran : huit équipements
       de démonstration se comptaient parmi les 63 du parc et parmi les 52 non
       raccordés, et ils avaient engendré huit des douze NC ouvertes. Poser le
       filtre écran par écran aurait garanti d'en oublier un — et un compteur
       oublié est exactement ce qui a produit le défaut.
       ⚠️ Le filtre est un CONFORT : la vraie garde est côté moteur, qui
       n'engendre plus rien depuis un objet marqué. Ici on nettoie la vue. */
    const garderRecette = get().inclureRecette && !!get().session?.recette?.ouvert;
    const equipsVus = garderRecette ? equips : (equips as GedEquipement[]).filter((e) => e.sandbox !== true);
    set({
      docs, demandes, settings, ncs, actions, recls, audits, rdds, equips: equipsVus, fourns, lots,
      cqis, eeqs, analytes, liens, copies, loading: false, backendError: settings === null,
      cartesCqi: (cqiServi as { cartes: GedCqiCarteResume[] }).cartes,
      recapCqi: (cqiServi as { recapitulatif: RecapCqi | null }).recapitulatif,
    });
  },

  // Une navigation ordinaire VIDE le chemin : cliquer « Non-conformités » dans
  // la barre n'est pas suivre un fil depuis un équipement, c'est changer de
  // sujet. Garder la pile ferait proposer un retour vers une fiche qu'on a
  // délibérément quittée — un fil d'Ariane qui ment est pire que pas de fil.
  // Changer d'écran EFFACE le message en cours. Un échec appartient au geste
  // qui l'a produit et à l'écran où il a eu lieu : le voir flotter au-dessus
  // d'un autre registre, parfois par-dessus une action qui vient de réussir,
  // le rendait à la fois inquiétant et incompréhensible (recette A2).
  go: (v) => { get().poserHistorique(); set({ view: v, smqFiltre: null, pileRetour: [], pilePanneaux: [], ...sansToast() }); },
  goFiltre: (v, filtre) => { get().poserHistorique(); set({ view: v, smqFiltre: filtre, pileRetour: [], pilePanneaux: [], ...sansToast() }); },

  ouvrirDepuis: (origine, cible) => { get().poserHistorique(); set((s) => ({
    ...sansToast(),
    pileRetour: [
      ...s.pileRetour,
      {
        view: s.view,
        libelle: origine,
        selection: {
          selectedNC: s.selectedNC, selectedAction: s.selectedAction, selectedRecl: s.selectedRecl,
          selectedAudit: s.selectedAudit, selectedRdd: s.selectedRdd, selectedEquip: s.selectedEquip,
          selectedFourn: s.selectedFourn, selectedCode: s.selectedCode,
        },
        // Ce qui manquait au retour (12/08/2026) : le filtre, l'endroit où l'on
        // lisait, et l'onglet ouvert. On revenait à la bonne fiche, jamais à
        // l'endroit où l'on en était.
        smqFiltre: s.smqFiltre,
        scrollY: typeof window === 'undefined' ? 0 : window.scrollY,
        scrollsInternes: capturerScrolls(),
        onglets: s.onglets,
      },
      // Borne de sûreté : un aller-retour en boucle entre deux fiches liées
      // empilerait sans fin. Huit étapes couvrent tout chemin réel.
    ].slice(-8),
    view: cible.view,
    smqFiltre: null,
    ...(cible.cle ? { [cible.cle]: cible.valeur ?? null } : {}),
  })); },

  retourPile: () => set((s) => {
    const pile = s.pileRetour.slice();
    const etape = pile.pop();
    if (!etape) return {};
    // `scrollY` est reposé APRÈS le rendu de l'écran retrouvé : le Shell le lit
    // dans `scrollARestaurer` (il ne peut pas se faire ici, la page n'a pas
    // encore la hauteur de l'écran d'arrivée).
    return {
      ...etape.selection, view: etape.view, smqFiltre: etape.smqFiltre,
      onglets: etape.onglets, pileRetour: pile, scrollARestaurer: etape.scrollY, scrollsInternesARestaurer: etape.scrollsInternes || [],
      ...sansToast(),
    };
  }),

  scrollRestaure: () => set({ scrollARestaurer: null, scrollsInternesARestaurer: [] }),

  /**
   * Aller à un écran EN DÉSIGNANT l'objet à y ouvrir — le remplaçant du couple
   * `select(...)` puis `go(...)`, qui posait un défaut discret : `go` empile
   * l'historique à partir de l'état COURANT, or `select` l'avait déjà modifié.
   * L'étape mémorisée portait donc l'ancien écran avec la NOUVELLE sélection,
   * et reculer restaurait un état qui n'avait jamais existé.
   *
   * À réserver aux vraies navigations — la recherche, un registre. Un objet
   * LIÉ, lui, s'ouvre en panneau (`ouvrirPanneau`) : on ne quitte pas sa page.
   */
  /**
   * L'ADRESSE commande l'état — premier rendu, « Précédent », « Suivant », F5.
   *
   * N'empile RIEN dans l'historique interne : le navigateur tient déjà le sien,
   * et doubler les deux ferait qu'un « Précédent » demanderait deux clics.
   */
  appliquerAdresse: (chemin) => {
    const cible = lireChemin(chemin);
    if (!cible.connue) {
      // Une adresse inconnue se DIT. Reposer sur l'accueil sans un mot ferait
      // passer un lien mort pour un lien valide — le destinataire chercherait
      // longtemps une fiche qu'on ne lui a jamais vraiment envoyée.
      set({
        view: 'dash', lienEnAttente: null,
        erreurLien: `L’adresse « ${chemin} » ne correspond à aucun écran de l’application. `
          + 'Le lien est peut-être tronqué (une adresse coupée en deux par un logiciel de messagerie) '
          + 'ou vient d’une version différente.',
      });
      return;
    }
    /* ⚠️ TOUTES les sélections sont EFFACÉES avant d'appliquer celle de
       l'adresse (relevé du 27/08/2026, P0.1).

       Sans cela, revenir de `/non-conformites/BIO24-NC-2026-014` à
       `/non-conformites` — bouton « Précédent », geste de retour, Alt+← —
       changeait l'adresse mais laissait `selectedNC` en place : la fiche restait
       ouverte, plein écran, alors que l'URL disait la liste. Rien ne bougeait,
       l'utilisateur appuyait une seconde fois, et il quittait l'application.

       L'adresse est la SOURCE : ce qu'elle ne nomme pas n'est pas ouvert. C'est
       ce qui rend le bouton « Précédent » du navigateur utilisable, et c'est la
       seule règle qui tienne sans qu'on ait à y repenser à chaque nouvel écran
       — la liste des clés est dérivée de la table d'adressage. */
    const base: Partial<GedState> = {
      view: cible.vue, smqFiltre: null, pileRetour: [], pilePanneaux: [], erreurLien: null,
      ...Object.fromEntries(CLES_ADRESSABLES.map((k) => [k, null])),
    };
    if (!cible.reference) { set({ ...base, lienEnAttente: null } as GedState); return; }
    const a = ADRESSES[cible.vue];
    // Une clé DIRECTE porte la référence telle quelle : aucune résolution, et
    // donc aucune attente de registre (cf. `Adressable.directe`).
    if (a?.directe && a.cle) {
      set({ ...base, [a.cle]: cible.reference, lienEnAttente: null } as GedState);
      return;
    }
    // Les autres fiches se désignent par référence : la résolution attend les
    // données (cf. `useAdresse`), et l'écran affiche la liste en attendant.
    set({ ...base, lienEnAttente: { vue: cible.vue, reference: cible.reference } } as GedState);
  },

  naviguerVers: (v, cible) => {
    get().poserHistorique();
    set({
      view: v, smqFiltre: null, pileRetour: [], pilePanneaux: [],
      ...(cible?.cle ? { [cible.cle]: cible.valeur ?? null } : {}),
      ...sansToast(),
    });
  },

  /* ── Historique de visite et flèche « revenir » (12/08/2026) ──────────────
     Distinct du FIL D'ARIANE, et volontairement.

     Le fil (`pileRetour`) dit « vous êtes ici DEPUIS telle fiche » : il se vide
     quand on change délibérément de sujet, sinon il proposerait de revenir à
     une fiche qu'on a justement quittée. L'historique, lui, ne juge rien : il
     retient les écrans traversés, comme le ferait le bouton « Précédent » d'un
     navigateur — que cette application n'a pas, faute d'adresse par écran.

     `naviguerVers` est le point de passage UNIQUE de toute navigation : c'est
     lui qui empile. Ce qui n'y passe pas ne sera pas dans l'historique. */
  poserHistorique: () => set((s) => {
    const derniere = s.historique[s.historique.length - 1];
    const etape: EtapeRetour = {
      view: s.view,
      libelle: '',
      selection: {
        selectedNC: s.selectedNC, selectedAction: s.selectedAction, selectedRecl: s.selectedRecl,
        selectedAudit: s.selectedAudit, selectedRdd: s.selectedRdd, selectedEquip: s.selectedEquip,
        selectedFourn: s.selectedFourn, selectedCode: s.selectedCode,
      },
      smqFiltre: s.smqFiltre,
      scrollY: typeof window === 'undefined' ? 0 : window.scrollY,
      scrollsInternes: capturerScrolls(),
      onglets: s.onglets,
    };
    // Deux fois le même écran avec la même sélection n'est pas une étape : ce
    // serait une flèche qui ne fait rien, le pire des boutons.
    const memeEndroit = derniere
      && derniere.view === etape.view
      && JSON.stringify(derniere.selection) === JSON.stringify(etape.selection);
    if (memeEndroit) return {};
    // Partir ailleurs efface l'avenir : la branche qu'on n'a pas reprise n'est
    // plus atteignable, exactement comme dans un navigateur.
    return { historique: [...s.historique, etape].slice(-25), avenir: [] };
  }),
  /* ═══════════════════════════════════════════════════════════════════════
   * ⚠️ LES FLÈCHES DE L'APPLICATION DÉLÈGUENT AU NAVIGATEUR (28/08/2026).
   *
   * ── Le défaut qu'elles avaient ─────────────────────────────────────────
   *
   * L'en-tête portait ses propres « Précédent / Suivant » à quinze pixels de
   * ceux de Chrome. Deux historiques concurrents, donc deux vérités : ouvrir
   * une fiche poussait une entrée dans l'adresse SANS empiler dans
   * `historique`, et reculer par le navigateur ne dépilait pas `historique`.
   * Au bout de trois gestes, les deux flèches ne menaient plus au même
   * endroit, et rien ne disait laquelle avait raison.
   *
   * ── Pourquoi déléguer plutôt que synchroniser ──────────────────────────
   *
   * Synchroniser deux piles, c'est se donner deux occasions de diverger à
   * chaque navigation — et l'on ne s'en aperçoit qu'après coup, sur un geste
   * qui n'aboutit pas. Il n'y a désormais qu'UNE pile : celle du navigateur.
   * Les flèches de l'application appellent `history.back()` / `forward()`,
   * exactement comme celles de Chrome ; `popstate` fait le reste, et la
   * position de défilement voyage dans `history.state` (`useAdresse`).
   *
   * ⚠️ `historique` et `avenir` SUBSISTENT, mais pour une seule raison : dire
   * ce qu'il y a derrière (« Revenir à Référentiel des analyses ») et griser
   * la flèche quand il n'y a rien. Ils ne COMMANDENT plus la navigation. Une
   * étiquette qui se trompe est un désagrément ; une navigation qui se trompe
   * était le défaut.
   * ═══════════════════════════════════════════════════════════════════════ */
  reculer: () => {
    if (typeof window === 'undefined') return;
    // La pile de libellés suit, pour que la flèche annonce la bonne
    // destination au coup d'après. Le déplacement, lui, est au navigateur.
    set((s) => {
      const pile = s.historique.slice();
      const etape = pile.pop();
      if (!etape) return {};
      const ici: EtapeRetour = {
        view: s.view, libelle: '',
        selection: {
          selectedNC: s.selectedNC, selectedAction: s.selectedAction, selectedRecl: s.selectedRecl,
          selectedAudit: s.selectedAudit, selectedRdd: s.selectedRdd, selectedEquip: s.selectedEquip,
          selectedFourn: s.selectedFourn, selectedCode: s.selectedCode,
        },
        smqFiltre: s.smqFiltre,
        scrollY: typeof window === 'undefined' ? 0 : window.scrollY,
        scrollsInternes: capturerScrolls(),
        onglets: s.onglets,
      };
      return { historique: pile, avenir: [...s.avenir, ici].slice(-25) };
    });
    window.history.back();
  },

  avancer: () => {
    if (typeof window === 'undefined') return;
    set((s) => {
      const pile = s.avenir.slice();
      const etape = pile.pop();
      if (!etape) return {};
      const ici: EtapeRetour = {
        view: s.view, libelle: '',
        selection: {
          selectedNC: s.selectedNC, selectedAction: s.selectedAction, selectedRecl: s.selectedRecl,
          selectedAudit: s.selectedAudit, selectedRdd: s.selectedRdd, selectedEquip: s.selectedEquip,
          selectedFourn: s.selectedFourn, selectedCode: s.selectedCode,
        },
        smqFiltre: s.smqFiltre,
        scrollY: typeof window === 'undefined' ? 0 : window.scrollY,
        scrollsInternes: capturerScrolls(),
        onglets: s.onglets,
      };
      return { avenir: pile, historique: [...s.historique, ici].slice(-25) };
    });
    window.history.forward();
  },


  /* ── Fiches liées en PANNEAU (12/08/2026) ────────────────────────────────
     Décision utilisateur : un objet lié s'ouvre PAR-DESSUS, jamais à la place.
     La page de départ garde ses filtres, son onglet et sa position parce
     qu'elle n'est tout simplement pas quittée — c'est la seule façon de ne
     rien avoir à restaurer. */
  pilePanneaux: [],
  ouvrirPanneau: (cible) => set((s) => ({
    // Empilement borné : un lien cliqué dans un panneau ouvre le suivant, mais
    // au-delà de quatre niveaux on ne sait plus d'où l'on vient.
    pilePanneaux: [...s.pilePanneaux, cible].slice(-4),
    /* ⚠️ On ne touche NI `docTab` NI `docVoirEnCours` ici : ils appartiennent à
       l'écran qui reste dessous. Une tâche ouvrant un panneau transmet son
       `onglet` et son `voirEnCours` en PROPS (`PanneauFiche` → `DocDossier` →
       `Fiche`), où ils deviennent l'état LOCAL du panneau — même raison que
       pour l'onglet local : sinon le panneau suivant hériterait de celui-ci. */
    ...sansToast(),
  })),
  fermerPanneau: () => set((s) => ({ pilePanneaux: s.pilePanneaux.slice(0, -1) })),
  fermerTousPanneaux: () => set({ pilePanneaux: [] }),
  panneauEnPleinePage: () => set((s) => {
    const courant = s.pilePanneaux[s.pilePanneaux.length - 1];
    if (!courant) return {};
    const VUE: Record<string, { view: GedView; cle?: CleSelection }> = {
      nc: { view: 'nc', cle: 'selectedNC' },
      action: { view: 'actions', cle: 'selectedAction' },
      doc: { view: 'doc', cle: 'selectedCode' },
      equip: { view: 'equip', cle: 'selectedEquip' },
      recl: { view: 'recl', cle: 'selectedRecl' },
      audit: { view: 'audits', cle: 'selectedAudit' },
      maintenance: { view: 'maintenances' },
    };
    const dest = VUE[courant.type];
    if (!dest) return {};
    return {
      pilePanneaux: [],
      pileRetour: [
        ...s.pileRetour,
        {
          view: s.view, libelle: courant.origine,
          selection: {
            selectedNC: s.selectedNC, selectedAction: s.selectedAction, selectedRecl: s.selectedRecl,
            selectedAudit: s.selectedAudit, selectedRdd: s.selectedRdd, selectedEquip: s.selectedEquip,
            selectedFourn: s.selectedFourn, selectedCode: s.selectedCode,
          },
          smqFiltre: s.smqFiltre,
          scrollY: typeof window === 'undefined' ? 0 : window.scrollY,
        scrollsInternes: capturerScrolls(),
          onglets: s.onglets,
        },
      ].slice(-8),
      view: dest.view,
      smqFiltre: null,
      ...(dest.cle ? { [dest.cle]: courant.valeur } : {}),
      ...sansToast(),
    };
  }),

  onglets: {},
  setOnglet: (cle, valeur) => set((s) => (s.onglets[cle] === valeur ? {} : { onglets: { ...s.onglets, [cle]: valeur } })),
  openSettings: (tab) => set({ settingsOpen: true, settingsTab: tab || null }),
  closeSettings: () => set({ settingsOpen: false, settingsTab: null }),
  /* ⚠️ `pilePanneaux: []` — un document en pleine page ne s'ouvre jamais SOUS un
     panneau. Depuis le 07/10/2026, les onze points d'entrée qui ouvraient un
     document en surcouche appellent `openDoc` (décision RQ : « un document
     s'ouvre directement en pleine page »), et plusieurs d'entre eux sont
     eux-mêmes DANS un panneau — le document lié d'une non-conformité, celui d'un
     dossier de vérification DVM. Sans cette ligne, la surcouche restait montée
     par-dessus la page qu'on venait d'ouvrir : l'écran paraissait n'avoir pas
     bougé, et le voile rendait le document inaccessible.
     Le chemin de retour est assuré par `poserHistorique` juste au-dessus. */
  openDoc: (code, tab, voirEnCours = false) => { get().poserHistorique(); set({ selectedCode: code, view: 'doc', docTab: tab || 'lecture', docVoirEnCours: voirEnCours, pilePanneaux: [] }); },
  setDocTab: (t) => set({ docTab: t }),
  setDocVoirEnCours: (v) => set({ docVoirEnCours: v }),
  setFilter: (k, v) => set({ [k]: v } as Partial<GedState>),

  loadMaintenances: async (du, au) => {
    const q = du && au ? `?du=${du}&au=${au}` : '';
    // Pas de `getAll` ici : la réponse n'est PAS une collection paginée mais un
    // registre agrégé (paramétrage, calendrier, lignes, occurrences). La
    // pagination est faite côté serveur, sur chaque collection lue.
    const d = await api(`ged/maintenances${q}`).catch(() => null);
    // `null` en cas d'échec, jamais un registre vide : « aucune maintenance
    // planifiée » et « la requête n'est pas passée » ne se disent pas pareil.
    set({ maintenances: (d as MaintenanceRegistre | null) ?? null });
  },

  loadJournal: async (docCode, heritage) => {
    // ⚠️ `sort=id:asc`, PAS `createdAt:asc`. Le journal est chaîné SANS colonne
    // `prev_hash` : l'ordre de lecture EST la chaîne. `created_at` est une date,
    // donc dépendante d'un fuseau — 278 maillons en portent un décalé de deux
    // heures, et sur 40 dossiers l'ordre par date INVERSE deux maillons. Un
    // auditeur lisait donc le journal de ces dossiers à l'envers. `id` est un
    // auto-increment : monotone, sans ex æquo, insensible au fuseau. Même clé
    // qu'`appendEvent` et que `scripts/verifier-chaine-journal.js`.
    const events = await getAll(`ged-events?filters[docCode][$eq]=${encodeURIComponent(docCode)}&sort=id:asc`);
    /* L'héritage se lit à part et se place AVANT : deux chaînes distinctes
       (le hash contient le docCode), affichées bout à bout — l'histoire du
       classeur d'abord, celle du registre applicatif ensuite. */
    const anciens = heritage
      ? await getAll(`ged-events?filters[docCode][$eq]=${encodeURIComponent(heritage)}&sort=id:asc`)
      : [];
    set((s) => ({ events: { ...s.events, [docCode]: [...anciens, ...events] } }));
  },

  showToast: (m, severite = 'info', action) => {
    set({ toast: m, toastSeverite: severite, toastAction: action ?? null });
    if (toastTimer) clearTimeout(toastTimer);
    // Une erreur annonce souvent une perte de travail (conflit 409) et demande
    // une action : elle reste affichée jusqu'à ce que l'utilisateur la ferme.
    // Le message de conflit fait 100 caractères — il disparaissait en 2,6 s,
    // avant même d'être lu (QA v2 UX-01). Seules les confirmations s'effacent.
    //
    // ⚠️ Ne PAS transformer ceci en effacement automatique : ce qui gênait en
    // recette (A2), ce n'était pas la persistance — c'était qu'un message
    // d'échec SURVIVE à la navigation et reste affiché quatre écrans plus
    // loin, au-dessus d'actions réussies. C'est `go` qui l'efface désormais.
    if (severite === 'erreur' || severite === 'indetermine') { toastTimer = null; return; }
    toastTimer = setTimeout(() => set({ toast: null, toastAction: null }), 2600);
  },
  dismissToast: () => {
    if (toastTimer) clearTimeout(toastTimer);
    set({ toast: null, toastAction: null });
  },
  demanderConfirmation: (question, opts) => new Promise<boolean>((resoudre) => {
    set({ confirmationGlobale: { question, titre: opts?.titre || 'Confirmer', confirmer: opts?.confirmer || 'Confirmer', danger: opts?.danger === true, resoudre } });
  }),
  repondreConfirmation: (ok) => {
    const c = get().confirmationGlobale;
    set({ confirmationGlobale: null });
    c?.resoudre(ok);
  },

  act: async (docId, action, body) => {
    try {
      const res = await post(`ged/docs/${docId}/${action}`, body);
      await get().refresh();
      const code = get().selectedCode;
      if (code) await get().loadJournal(code);
      // Retour visuel explicite pour les actions sans changement visible immédiat
      // (une relance n'ajoute pas d'accusé — sans toast elle semblait « ne rien faire »).
      //
      // GED-4 b — la relance ENVOIE désormais un courriel (canal du laboratoire,
      // celui de la 2FA). Le message ne dit donc plus « tracée » ; mais il ne dit
      // pas non plus « envoyée » en bloc : il rend le compte que le serveur a
      // réellement constaté, remis / en échec / sans adresse. Une panne SMTP doit
      // se VOIR ici, pas se deviner dans le journal. C'est la même exigence qui
      // avait fait retirer le mot « envoyée » la veille, appliquée à l'envers :
      // ce qu'on affiche doit être ce qui s'est produit.
      if (action === 'relance') {
        const n = typeof res?.relances === 'number' ? res.relances : (typeof res?.pending === 'number' ? res.pending : null);
        const remis = typeof res?.remis === 'number' ? res.remis : null;
        const echecs = typeof res?.echecs === 'number' ? res.echecs : 0;
        const sans = typeof res?.sansAdresse === 'number' ? res.sansAdresse : 0;
        const ignores = typeof res?.ignores === 'number' ? res.ignores : 0;
        const suite = [
          remis != null ? `courriel remis à ${remis}` : null,
          echecs ? `${echecs} en échec d’envoi` : null,
          sans ? `${sans} sans adresse` : null,
          ignores ? `${ignores} déjà relancé(s) récemment, non recomptés` : null,
        ].filter(Boolean).join(' · ');
        get().showToast(
          n != null ? `Relance de ${n} lecteur(s) devant accuser réception${suite ? ` — ${suite}.` : '.'}` : 'Relance émise.',
          echecs ? 'erreur' : undefined,
        );
      } else if (action === 'destinataire/retrait') {
        // Deux gestes derrière un seul appel : le serveur dit lequel il a fait.
        // Ne pas le relire ici afficherait « retiré » sur une bascule « pour
        // information » — c'est-à-dire annoncer la disparition d'une ligne qui
        // est toujours là, avec son accusé.
        get().showToast(res?.mode === 'pourAvenir'
          ? 'Plus aucun accusé n’est demandé à cette personne pour l’avenir — son accusé déjà donné est conservé.'
          : 'Destinataire retiré de la diffusion — le motif est inscrit au journal signé.');
      } else if (action === 'diffuse') {
        get().showToast('Diffusion émise — accusés de lecture demandés.');
      } else if (action === 'reprendre') {
        get().showToast('Document repris — circuit rouvert en révision.');
      } else if (action === 'gel') {
        // Le gel ne change ni le statut ni la version : sans message, l'écran
        // semblait ne rien faire — c'est le cas de la relance ci-dessus.
        const n = typeof res?.notifies === 'number' ? res.notifies : null;
        get().showToast(n != null
          ? `Applicabilité gelée — ${n} destinataire(s) invité(s) à en prendre connaissance.`
          : 'Applicabilité gelée — évolution du document suspendue.');
      } else if (action === 'degel') {
        get().showToast('Gel levé — le document reprend son cycle normal.');
      }
      return true;
    } catch (e) {
      get().showToast(e instanceof Error ? e.message : 'Action refusée', 'erreur');
      return false;
    }
  },

  createDemande: async (description, propositions) => {
    try {
      await post('ged/demandes', { description, propositions });
      await get().refresh();
      get().showToast('Demande soumise au RQ.');
      return true;
    } catch (e) {
      signalerEchecEcriture(get, e, 'Soumission de la demande',
        () => { void get().createDemande(description, propositions); });
      return false;
    }
  },

  decideDemande: async (id, decision, motif, assign) => {
    try {
      await post(`ged/demandes/${id}/decide`, { decision, motif, assign });
      await get().refresh();
      if (decision === 'approuvee' || decision === 'approuvee_mod') get().showToast('Demande approuvée — brouillon ouvert pour le rédacteur.');
      return true;
    } catch (e) {
      signalerEchecEcriture(get, e, 'Décision sur la demande',
        () => { void get().decideDemande(id, decision, motif, assign); });
      return false;
    }
  },

  addDemandeNote: async (id, texte) => {
    try {
      await post(`ged/demandes/${id}/note`, { texte });
      await get().refresh();
      return true;
    } catch (e) {
      signalerEchecEcriture(get, e, 'Ajout de la note au fil',
        () => { void get().addDemandeNote(id, texte); });
      return false;
    }
  },

  reopenDemande: async (id) => {
    try {
      await post(`ged/demandes/${id}/reopen`);
      await get().refresh();
      get().showToast('Demande rouverte — elle repart « à statuer », le motif du refus reste au fil.');
      return true;
    } catch (e) {
      signalerEchecEcriture(get, e, 'Réouverture de la demande',
        () => { void get().reopenDemande(id); });
      return false;
    }
  },

  loadEngagements: async (key) => {
    try {
      return await api(`ged/users/${encodeURIComponent(key)}/engagements`) as Engagements;
    } catch (e) {
      get().showToast(e instanceof Error ? e.message : 'Inventaire indisponible', 'erreur');
      return null;
    }
  },

  transferer: async (key, payload) => {
    try {
      const res = await post(`ged/users/${encodeURIComponent(key)}/transfer`, payload);
      await get().refresh();
      const n = (res?.resume || []).length;
      get().showToast(n ? `Transfert appliqué — ${n} élément(s) réaffecté(s).` : 'Rien à transférer.');
      return true;
    } catch (e) {
      get().showToast(e instanceof Error ? e.message : 'Transfert refusé', 'erreur');
      return false;
    }
  },

  // Un lien passe TOUJOURS par le moteur : lui seul vérifie que les deux
  // extrémités existent. C'est l'absence de cette vérification qui laissait des
  // références pointer dans le vide pendant des mois.
  creerLien: async (l) => get().smq('ged/liens', l, 'Lien créé.'),
  retirerLien: async (documentId) => {
    try {
      await api(`ged/liens/${documentId}`, { method: 'DELETE' });
      await get().refresh();
      get().showToast('Lien retiré.');
      return true;
    } catch (e) {
      get().showToast(e instanceof Error ? e.message : 'Retrait refusé', 'erreur');
      return false;
    }
  },

  smq: async (path, body, okToast, contexte, methode) => {
    // Le témoin s'allume AVANT l'appel et s'éteint dans le `finally` : sur le
    // chemin d'erreur comme sur celui du succès. Un témoin qui reste allumé
    // après un échec fait croire que l'application travaille encore.
    set((e) => ({ ecrituresEnCours: e.ecrituresEnCours + 1 }));
    try {
      if (methode === 'DELETE') {
        /* ⚠️ Le CORPS d'un DELETE : le mandataire Next le transmet bien (il le
           jetait jusqu'au 14/08), et le serveur l'exige ici — le motif de
           suppression y voyage. */
        await api(path, { method: 'DELETE', body: JSON.stringify(body ?? {}) });
      } else {
        await post(path, body);
      }
      await get().refresh();
      if (okToast) get().showToast(okToast);
      return true;
    } catch (e) {
      signalerEchecEcriture(get, e, contexte, () => { void get().smq(path, body, okToast, contexte, methode); });
      return false;
    } finally {
      set((e) => ({ ecrituresEnCours: Math.max(0, e.ecrituresEnCours - 1) }));
    }
  },

  interroger: async (path, body) => {
    try {
      // ⚠️ SANS CORPS, C'EST UN GET — et ce n'est pas un détail de confort.
      // La première version postait toujours. Or beaucoup de routes portent le
      // MÊME chemin en GET (lire) et en POST (créer) : `ged/eil`,
      // `ged/acces-audits`. Interroger en POST tapait donc sur la route de
      // CRÉATION, qui répondait 400, et l'écran affichait « Aucun échange
      // organisé » — une affirmation fausse, sur une base qui en contenait un.
      // Défaut trouvé le 28/08/2026 en faisant tourner l'écran pour de vrai :
      // ni les types ni les tests unitaires ne pouvaient le voir.
      return await (body === undefined ? api(path) : post(path, body));
    } catch (e) {
      get().showToast(e instanceof Error ? e.message : 'Interrogation refusée', 'erreur');
      return null;
    }
  },

  smqData: async (path, body, okToast, contexte) => {
    set((e) => ({ ecrituresEnCours: e.ecrituresEnCours + 1 }));
    try {
      const data = await post(path, body);
      await get().refresh();
      if (okToast) get().showToast(okToast);
      return data ?? ({} as never);
    } catch (e) {
      signalerEchecEcriture(get, e, contexte, () => { void get().smqData(path, body, okToast, contexte); });
      return null;
    } finally {
      set((e) => ({ ecrituresEnCours: Math.max(0, e.ecrituresEnCours - 1) }));
    }
  },

  /**
   * LA LECTURE VAUT PRISE EN CHARGE (26/08/2026) — le remplaçant des boutons
   * « Passer en analyse » et « Prendre en charge », retirés le même jour.
   *
   * ⚠️ SILENCIEUSE PAR CONSTRUCTION, et chacun de ses trois silences est voulu :
   *
   *  · **pas de toast d'erreur.** Elle part à chaque ouverture de dossier, par
   *    tout le monde. Un échec réseau sur une consultation parfaitement
   *    légitime ne doit pas faire surgir « l'enregistrement a échoué » à
   *    quelqu'un qui n'a rien enregistré. Le serveur répond d'ailleurs 200 sans
   *    rien faire dans l'immense majorité des cas — `bascule: false` est un
   *    résultat normal, pas une panne.
   *  · **pas de témoin d'écriture.** Elle n'incrémente pas `ecrituresEnCours` :
   *    le tube tournerait à chaque ouverture de fiche, pour une écriture qui
   *    n'aura probablement pas lieu.
   *  · **`refresh()` SEULEMENT si ça a basculé.** Rafraîchir à chaque ouverture
   *    rechargerait tout le magasin pour rien et ferait clignoter l'écran ;
   *    ne jamais rafraîchir laisserait la fiche afficher « déclarée » alors que
   *    le serveur vient de la passer en analyse — l'utilisateur verrait un état
   *    faux jusqu'à sa prochaine navigation.
   */
  /* Bascule d'affichage : on RECHARGE, parce que le filtre s'applique à
     l'entrée du magasin. Filtrer à la volée obligerait chaque écran à
     redemander la préférence — et à l'oublier. */
  setInclureRecette: (v) => { set({ inclureRecette: v }); void get().refresh(); },

  signalerLecture: async (registre, documentId) => {
    if (!documentId) return;
    try {
      const r = await post(`ged/${registre}/${documentId}/lu`) as { bascule?: boolean } | null;
      if (r?.bascule) await get().refresh();
    } catch { /* consultation : on ne dérange pas le lecteur */ }
  },

  /* Les points de règle — l'énoncé des choix de BIO24, affiché derrière le « i »
     là où le choix se fait.

     Servi par le MOTEUR et non recopié ici : les valeurs qu'un encart cite
     (seuils de gravité, plancher, champs exigés) sont dérivées du code qui les
     applique. Un catalogue recopié dans ce dépôt serait un miroir — il aurait
     raison le jour où on l'écrit, et tort six mois plus tard.

     Au démarrage et une seule fois : c'est du texte de procédure, il ne change
     pas d'une heure à l'autre, et un écran porte une dizaine de « i » qui
     feraient autant d'allers-retours au survol. */
  chargerRegles: async () => {
    try {
      const r = await api('ged/regles') as { data?: RegleServie[] } | null;
      set({ regles: r?.data ?? [] });
    } catch {
      // Silencieux, et c'est délibéré : la règle est un CONFORT de lecture, pas
      // une fonction attendue. Sans elle les « i » ne s'affichent pas et
      // l'écran fonctionne comme avant — déranger d'un toast pour ça ferait
      // passer une absence d'aide pour une panne.
      set({ regles: [] });
    }
  },

  chargerIA: async () => {
    try {
      const etat = await api('ia/etat');
      set({
        iaDisponible: !!etat?.disponible,
        // `disponible: false` avec une réponse REÇUE, c'est « aucun serveur
        // d'inférence n'est configuré » — pas une panne. Le serveur a répondu,
        // et ce qu'il répond est « il n'y a rien ici ».
        iaEtat: etat?.disponible ? 'pret' : 'eteint',
        iaTaches: etat?.taches ?? [],
        iaEntretienNC: etat?.entretienNC === true,
      });
    } catch {
      // Pas de toast : l'assistance est un confort, pas une fonction attendue
      // par tout le monde au démarrage. Mais l'échec est MÉMORISÉ tel quel —
      // l'écran qui doit l'expliquer ne dira pas « pas encore installée » là où
      // la vérité est « le serveur n'a pas répondu ».
      set({ iaDisponible: false, iaEtat: 'injoignable', iaTaches: [], iaEntretienNC: false });
    }
  },

  lireAudience: async (docId) => {
    try {
      return await api(`ged/docs/${docId}/audience`);
    } catch (e) {
      get().showToast(e instanceof Error ? e.message : 'Audience illisible', 'erreur');
      return null;
    }
  },

  apercuAudienceBrouillon: async (docId, audience) => {
    try {
      return await post(`ged/docs/${docId}/audience/apercu`, { audience });
    } catch {
      return null;
    }
  },

  enregistrerAudience: async (docId, audience) => {
    try {
      await post(`ged/docs/${docId}/audience`, { audience });
      await get().refresh();
      const code = get().selectedCode;
      if (code) await get().loadJournal(code);
      get().showToast(audience ? 'Audience enregistrée.' : 'Audience retirée.');
      return true;
    } catch (e) {
      get().showToast(e instanceof Error ? e.message : 'Audience refusée', 'erreur');
      return false;
    }
  },

  entretienNC: async (echanges, conclure) => {
    // Échéance du client au-dessus de celle du serveur (45 s) : c'est la
    // sienne qui doit s'exprimer, avec sa phrase.
    const stop = new AbortController();
    const minuteur = setTimeout(() => stop.abort(), 60_000);
    const repli = 'L’assistant n’a pas répondu. Réessayez, ou remplissez la fiche à la main.';
    try {
      const res = await fetch('/api/ged/ia/entretien-nc', {
        method: 'POST', credentials: 'include', signal: stop.signal,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ echanges, conclure }),
      });
      const data = await res.json().catch(() => null);
      if (!res.ok) {
        const brut = data?.error?.message || data?.error;
        return { ok: false, message: typeof brut === 'string' && brut ? brut : repli };
      }
      if (!data || typeof data !== 'object') return { ok: false, message: repli };
      return { ok: true, reponse: data as Record<string, unknown> };
    } catch (e) {
      return {
        ok: false,
        message: e instanceof Error && e.name === 'AbortError'
          ? 'L’assistant a mis trop de temps à répondre. Réessayez, ou remplissez la fiche à la main.'
          : repli,
      };
    } finally {
      clearTimeout(minuteur);
    }
  },

  assisterIA: async (tache, entree, contexte, options) => {
    const echec = (motif: EchecIA, message: string): ResultatIA => {
      if (!options?.silencieux) get().showToast(message, 'erreur');
      return { ok: false, motif, message };
    };
    // Appel ÉCRIT ICI plutôt que par `post()` : il faut le code HTTP (que
    // `api()` jette avec le message) et une échéance propre au client. Le
    // serveur d'inférence a la sienne (IA_TIMEOUT_MS, 60 s par défaut) et
    // répond alors proprement ; celle-ci ne sert qu'au cas où la chaîne
    // entière reste pendue — proxy compris — et où personne ne répondrait.
    const stop = new AbortController();
    const minuteur = setTimeout(() => stop.abort(), DELAI_IA_CLIENT_MS);
    try {
      const res = await fetch('/api/ged/ia/assister', {
        method: 'POST', credentials: 'include', signal: stop.signal,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ tache, entree, contexte }),
      });
      const data = await res.json().catch(() => null);
      if (!res.ok) {
        const brut = data?.error?.message || data?.error;
        const message = typeof brut === 'string' && brut ? brut : 'Assistance indisponible';
        return echec(motifDepuisReponse(res.status, message), message);
      }
      const suggestion = data?.suggestion;
      if (!suggestion || typeof suggestion !== 'object') {
        return echec('panne', 'L’assistance n’a rien renvoyé d’exploitable.');
      }
      return { ok: true, suggestion: suggestion as Record<string, unknown> };
    } catch (e) {
      if (e instanceof Error && e.name === 'AbortError') {
        return echec('delai', 'L’assistance a mis trop de temps à répondre — rien n’a été enregistré.');
      }
      return echec('panne', e instanceof Error ? e.message : 'Assistance indisponible');
    } finally {
      clearTimeout(minuteur);
    }
  },

  select: (k, id) => set({ [k]: id } as Partial<GedState>),

  // ---- Équipements : pièces jointes & étiquette ---------------------------
  // Le fichier part en base64 dans le corps JSON, comme pour les documents
  // bureautiques : le reste de l'application ne pose aucun `multipart`, en
  // ajouter un ici obligerait à dupliquer la gestion de session et d'erreurs.
  joindreFichierEquip: async (equipId, cible, fichier) => {
    try {
      // Taille ET contenu réel, contrôlés avant l'encodage base64 : le message
      // nomme le poids du fichier et la limite (SEC-10 b).
      const refus = await refusDepot('technique', fichier, 'La pièce jointe');
      if (refus) throw new Error(refus);
      const base64 = await enBase64(fichier);
      if (cible.type === 'intervention') {
        await post(`ged/equip/${equipId}/intervention/${cible.id}/fichier`, { name: fichier.name, base64 });
      } else {
        // `type` n'est qu'un discriminant côté client : le serveur a deux routes
        // distinctes et n'en a pas l'usage — on ne le lui envoie pas.
        await post(`ged/equip/${equipId}/piece`, {
          nature: cible.nature, libelle: cible.libelle, reference: cible.reference,
          version: cible.version, url: cible.url, dateDocument: cible.dateDocument,
          dateValidite: cible.dateValidite,
          name: fichier.name, base64,
        });
      }
      await get().refresh();
      get().showToast('Pièce jointe enregistrée.');
      return true;
    } catch (e) {
      get().showToast(e instanceof Error ? e.message : 'Envoi refusé', 'erreur');
      return false;
    }
  },
  analyserPieceEquip: async (fichier) => {
    try {
      const refus = await refusDepot('technique', fichier, 'La pièce jointe');
      if (refus) throw new Error(refus);
      const base64 = await enBase64(fichier);
      return await post('ged/equip/piece/analyser', { name: fichier.name, base64 }) as PieceAnalysee;
    } catch (e) {
      get().showToast(e instanceof Error ? e.message : 'Fichier refusé', 'erreur');
      return null;
    }
  },
  // Une notice constructeur n'est publiée que sur le portail client du fabricant :
  // on enregistre alors QUEL document fait foi et OÙ il se trouve, sans en verser
  // une copie de provenance incertaine — dont la version serait inconnue, donc
  // sans valeur de preuve.
  enregistrerReference: async (equipId, meta) => {
    try {
      await post(`ged/equip/${equipId}/piece`, meta);
      await get().refresh();
      get().showToast('Référence documentaire enregistrée.');
      return true;
    } catch (e) {
      get().showToast(e instanceof Error ? e.message : 'Enregistrement refusé', 'erreur');
      return false;
    }
  },
  retirerPieceEquip: async (equipId, pieceId) => {
    try {
      await api(`ged/equip/${equipId}/piece/${pieceId}`, { method: 'DELETE' });
      await get().refresh();
      get().showToast('Pièce retirée — le retrait est tracé au journal.');
      return true;
    } catch (e) {
      get().showToast(e instanceof Error ? e.message : 'Retrait refusé', 'erreur');
      return false;
    }
  },
  etiquetteEquip: async (equipId) => {
    try {
      return await api(`ged/equip/${equipId}/etiquette`);
    } catch (e) {
      get().showToast(e instanceof Error ? e.message : 'Étiquette indisponible', 'erreur');
      return null;
    }
  },
  impactSortieEquip: async (equipId) => {
    try {
      return await api(`ged/equip/${equipId}/impact-sortie`);
    } catch {
      // Lecture d'appoint pour un avertissement : une panne réseau ici ne doit
      // pas empêcher la RQ de sortir l'appareil du service, elle prive juste
      // l'écran de l'avertissement — silence, pas un `showToast` de plus.
      return null;
    }
  },

  // ---- Module RH ----------------------------------------------------------
  rhAcces: null,
  rhAccesErreur: false,
  rhChargement: 0,
  rhPersonnel: [],
  rhPostes: [],
  rhAffectations: [],
  rhPlanningModifiable: false,
  rhOuvertures: [],
  rhModelesOuverture: [],
  rhSemaine: lundiDe(new Date()),
  rhMois: new Date().toISOString().slice(0, 7),
  rhAffectationsMois: [],
  rhOuverturesMois: [],
  rhMoisCharge: null,

  // Interrogé pour TOUT le monde au démarrage : c'est la seule façon de savoir
  // s'il faut afficher la rubrique. La route ne renvoie aucune donnée RH.
  loadRhAcces: async () => {
    try {
      const rhAcces = await api('ged/rh/me');
      set({ rhAcces, rhAccesErreur: false });
    } catch {
      // Panne réseau/serveur : on ne touche pas à `rhAcces` (garder le dernier
      // état connu plutôt que le faire passer pour un refus) et on le signale
      // via `rhAccesErreur`, seul moyen de distinguer ça d'un vrai refus.
      set({ rhAccesErreur: true });
    }
  },

  loadRh: async (lundi) => {
    const acces = get().rhAcces;
    const droits = get().session.sections;
    // Lot N6 — le planning et les postes se lisent LARGEMENT ; le reste de la
    // rubrique reste réservé au gestionnaire RH. Sans cette distinction, un
    // technicien à qui le planning est ouvert ne chargeait rien du tout : la
    // garde de l'écran serait tombée sur une liste vide, ce qui se lit « il n'y
    // a pas de planning cette semaine » — un refus déguisé en constat.
    // ⚠️ La règle vit dans `lib/acces-planning.ts` et nulle part ailleurs : elle
    // était réécrite à la main ici et dans trois autres endroits, dont deux
    // l'avaient de travers (12/09/2026).
    /* ⚠️ LE RQ CHARGE LA LISTE DU PERSONNEL MÊME SANS LE RÔLE `rh` (02/10/2026) :
       la fiche se modifie par RQ OU RH. Si la route la lui refuse, le `.catch`
       ci-dessous rend une liste vide — la fiche masque alors ses boutons de
       saisie plutôt que d'éditer dans le vide. */
    const rq = get().session.roles.includes('rq');
    // Qui lit les habilitations (RQ, responsable technique) charge aussi le référentiel et les détenues.
    const seul = planningSeul(acces, droits) && !rq && !(droits?.rhhab?.voir);
    if (!rq && !peutLirePlanning(acces, droits)) return; // inutile de demander ce qui sera refusé
    const semaine = lundi || get().rhSemaine;
    const jours = joursSemaine(semaine);
    set((s) => ({ rhChargement: s.rhChargement + 1 }));
    try {
      const vide = <T,>(v: T) => Promise.resolve(v);
      const [postes, personnel, affectations, habilitations, detenues, contrats, integ] = await Promise.all([
        (droits?.postes?.voir === false ? vide(null) : api('ged/rh/postes').then((d) => d ?? null).catch(() => null)),
        seul ? vide([]) : api('ged/rh/personnel').then((d) => d?.personnel ?? []).catch(() => []),
        api(`ged/rh/affectations?du=${jours[0]}&au=${jours[6]}`).then((d) => d ?? null).catch(() => null),
        seul ? vide([]) : api('ged/rh/habilitations').then((d) => d?.habilitations ?? []).catch(() => []),
        seul ? vide([]) : api('ged/rh/habilitations-detenues').then((d) => d?.detenues ?? []).catch(() => []),
        seul ? vide([]) : api('ged/rh/contrats').then((d) => d?.contrats ?? []).catch(() => []),
        seul ? vide(null) : api('ged/rh/integrations').then((d) => d ?? null).catch(() => null),
      ]);
      set({
        /* Les postes : la route dédiée, à défaut ceux que le planning embarque
           (section « Postes de travail » fermée à ce profil — 04/10/2026). */
        rhPostes: postes?.postes ?? affectations?.postes ?? [],
        rhModelesOuverture: postes?.modelesOuverture ?? affectations?.modelesOuverture ?? [],
        rhPersonnel: personnel,
        rhAffectations: affectations?.affectations ?? [],
        rhPlanningModifiable: !!affectations?.modifiable,
        rhOuvertures: affectations?.ouvertures ?? [], rhSemaine: semaine,
        rhHabilitations: habilitations, rhDetenues: detenues, rhContrats: contrats,
        rhIntegrations: integ?.integrations ?? [],
        /* ⚠️ `phasesReference` n'existe plus : la procédure INS.G1.01 a été
           remplacée par F-PROC-01 v03, et ses quatre phases par processus par
           les quatorze actions du jour J. On lit `accueilReference` et on le
           replie dans la forme attendue par l'écran — une seule « phase ». */
        rhPhasesReference: integ?.accueilReference
          ? [{ cle: 'accueil', titre: 'Accueil du jour J', objectifs: [], responsables: [],
            activites: (integ.accueilReference as { id: string; libelle: string }[])
              .map((a) => ({ id: a.id, texte: a.libelle, fait: false, date: '', par: '', note: '' })) }]
          : [],
        rhPochetteReference: integ?.pochetteReference ?? [],
      });
    } finally {
      set((s) => ({ rhChargement: s.rhChargement - 1 }));
    }
  },

  changerStatutPersonne: async (orgId, statut, motif) => {
    try {
      await post(`ged/rh/personnel/${encodeURIComponent(orgId)}/statut`, { statut, motif });
      // `refresh` ET `loadRh` : le statut vit des deux côtés — l'organigramme
      // (module Qualité) le grise, le dossier (module RH) le porte. Ne
      // rafraîchir qu'un seul laisserait l'autre écran afficher l'ancien état.
      await Promise.all([get().refresh(), get().loadRh()]);
      get().showToast(statut === 'actif' ? 'Dossier réactivé.'
        : statut === 'suspendu' ? 'Dossier suspendu.' : 'Dossier archivé.');
      return true;
    } catch (e) {
      get().showToast(e instanceof Error ? e.message : 'Changement de statut refusé', 'erreur');
      return false;
    }
  },

  enregistrerContrat: async (c) => {
    try {
      await post('ged/rh/contrats', c);
      /* `loadRh` : le contrat porte le droit à congé réel, et le parcours
         d'intégration lit cette étape. Sans rechargement, l'écran continuerait
         d'annoncer « aucun contrat » juste après en avoir posé un. */
      await get().loadRh();
      get().showToast('Contrat enregistré.');
      return true;
    } catch (e) {
      get().showToast(e instanceof Error ? e.message : 'Enregistrement refusé', 'erreur');
      return false;
    }
  },

  enregistrerContratId: async (c) => {
    try {
      const d = await post('ged/rh/contrats', c);
      await get().loadRh();
      get().showToast('Contrat enregistré.');
      return String(d?.contrat?.documentId || c.documentId || '') || null;
    } catch (e) {
      get().showToast(e instanceof Error ? e.message : 'Enregistrement refusé', 'erreur');
      return null;
    }
  },

  joindrePieceContrat: async (contratId, fichier) => {
    try {
      /* Même garde que les pièces du dossier d'intégration : taille et contenu
         RÉEL contrôlés avant l'envoi (un exécutable renommé « contrat.pdf »
         n'entre pas). Le serveur revérifie. */
      const refus = await refusDepot('pieceRh', fichier, 'Le contrat signé');
      if (refus) throw new Error(refus);
      /* JSON base64, comme `deposerPiece` et `joindreFichierEquip` : le proxy de
         l'application ne pose aucun `multipart`. Le moteur accepte les deux
         (`lirePieceRequete`) et revérifie format et taille. */
      const fichierData = await enBase64(fichier);
      await post(`ged/rh/contrats/${encodeURIComponent(contratId)}/piece`, { fichierNom: fichier.name, fichierData });
      get().showToast('Contrat signé joint.');
      return true;
    } catch (e) {
      get().showToast(e instanceof Error ? e.message : 'Dépôt refusé', 'erreur');
      return false;
    }
  },

  retirerPieceContrat: async (contratId) => {
    try {
      await del(`ged/rh/contrats/${encodeURIComponent(contratId)}/piece`);
      get().showToast('Pièce retirée du contrat.');
      return true;
    } catch (e) {
      get().showToast(e instanceof Error ? e.message : 'Suppression refusée', 'erreur');
      return false;
    }
  },

  ouvrirPieceContrat: async (contratId, nom) => {
    try {
      /* Lecture JSON (`fichierData` en base64) puis fichier local : chaque lecture
         est tracée côté serveur (journal « pièce de contrat consultée »). */
      const d = await api(`ged/rh/contrats/${encodeURIComponent(contratId)}/piece`);
      const bin = atob(String(d?.fichierData || ''));
      const octets = new Uint8Array(bin.length);
      for (let i = 0; i < bin.length; i += 1) octets[i] = bin.charCodeAt(i);
      const blob = new Blob([octets], { type: String(d?.type || 'application/octet-stream') });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = nom || String(d?.nom || 'contrat'); a.rel = 'noopener';
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 30_000);
    } catch (e) {
      get().showToast(e instanceof Error ? e.message : 'Ouverture refusée', 'erreur');
    }
  },

  changerIdentifiantCompte: async (orgId, identifiant) => {
    try {
      await post(`ged/rh/personnel/${encodeURIComponent(orgId)}/identifiant`, { identifiant });
      await Promise.all([get().refresh(), get().loadRh()]);
      get().showToast('Identifiant de connexion modifié.');
      return true;
    } catch (e) {
      get().showToast(e instanceof Error ? e.message : 'Changement refusé', 'erreur');
      return false;
    }
  },

  chargerContrats: async (orgId) => {
    const d = await api('ged/rh/contrats').catch(() => null);
    return ((d?.contrats ?? []) as import('@/lib/types').RhContrat[])
      .filter((c) => c.personnelOrgId === orgId);
  },

  // Le brut est VIDÉ au changement de semaine, pas seulement invalidé : sans
  // cela, la semaine précédente resterait affichée le temps de la relecture —
  // des pointages justes, sous la mauvaise date. Le pire des affichages.
  setRhSemaine: (lundi) => {
    set({ rhSemaine: lundi, rhPointages: [], rhPointagesCharges: null });
    void get().loadRh(lundi); void get().loadPresences(lundi);
  },

  setRhMois: (mois) => {
    set({ rhMois: mois });
    void get().loadRhMois(mois);
  },

  /* A3 — le mois entier en une requête. La route `affectations` accepte déjà
     une plage quelconque et pagine côté serveur (`tout`, 100 par 100) : rien à
     ajouter au moteur. L'état d'ouverture est calculé par le serveur sur la
     MÊME plage — la règle de préséance (exception > férié > règle du jour) ne
     doit exister qu'à un seul endroit. */
  lireAffectationsDuJour: async (date) => {
    const d = await api(`ged/rh/affectations?du=${date}&au=${date}`).catch(() => null) as { affectations?: RhAffectation[]; data?: RhAffectation[] } | null;
    return d?.affectations || d?.data || [];
  },
  loadRhMois: async (mois) => {
    /* ⚠️ LE MÊME REPLI QUE `loadRh`, ET IL MANQUAIT ICI (corrigé le
       12/09/2026). Cette ligne testait le seul `rhAcces.acces`, c'est-à-dire le
       rôle `rh` — alors que la section `planning` porte la règle
       `lectureLarge` (`backend/src/ged-sections.ts`) et que la route sert le
       mois à qui a `planning.voir`. La vue Semaine avait reçu la distinction,
       la vue Mois non : un technicien basculait sur « Mois » et lisait une
       grille VIDE, sans un mot — c'est-à-dire « personne n'est planifié ce
       mois-ci », au lieu de « je n'ai pas demandé ». Retour Manel du 11/09 :
       elle porte `redacteur/verificateur/lecteur`, pas `rh`.
       Ne pas refactoriser les deux en une garde commune sans y regarder à
       deux fois : `loadRh` s'en sert AUSSI pour décider quelles listes il ne
       demande pas (personnel, habilitations, contrats), ce que le mois n'a
       pas à faire. */
    if (!peutLirePlanning(get().rhAcces, get().session.sections)) return;
    const m = mois || get().rhMois;
    if (get().rhMoisCharge === m) return;
    const [a, mo] = m.split('-').map(Number);
    const du = `${m}-01`;
    /* ⚠️ PAS DE `toISOString()` sur une date LOCALE (29/09/2026) : minuit du
       30/09 à Tunis (UTC+1) est le 29/09 à 23:00 en UTC, et le dernier jour du
       mois n'était jamais chargé — la garde du 30 manquait à la vue Mois. */
    const au = `${m}-${String(new Date(a, mo, 0).getDate()).padStart(2, '0')}`;
    set((st) => ({ rhChargement: st.rhChargement + 1 }));
    try {
      const d = await api(`ged/rh/affectations?du=${du}&au=${au}`).catch(() => null);
      // Le mois n'est marqué chargé QUE si la requête a abouti : sinon un
      // échec réseau figerait une grille vide en prétendant qu'elle est à jour.
      if (d) {
        set({
          rhAffectationsMois: d.affectations ?? [],
          rhOuverturesMois: d.ouvertures ?? [],
          rhMoisCharge: m,
        });
      }
    } finally {
      set((st) => ({ rhChargement: st.rhChargement - 1 }));
    }
  },

  ouvrirIntegration: async (orgId, dateDebut) => {
    try {
      await post('ged/rh/integrations', { personnelOrgId: orgId, dateDebut });
      await get().loadRh();
      get().showToast('Dossier d’intégration ouvert.');
      return true;
    } catch (e) {
      get().showToast(e instanceof Error ? e.message : 'Ouverture refusée', 'erreur');
      return false;
    }
  },

  cocherActivite: async (id, activiteId, fait, note) => {
    try {
      await post(`ged/rh/integrations/${encodeURIComponent(id)}/activite`, { activiteId, fait, note });
      await get().loadRh();
      return true;
    } catch (e) {
      get().showToast(e instanceof Error ? e.message : 'Enregistrement refusé', 'erreur');
      return false;
    }
  },

  /* La pochette : on enregistre l'ACTE DE REMISE, jamais la pièce. Trois des
     six sont générées depuis la fonction — les recopier les figerait. */
  remettrePiecePochette: async (id, cle, remise) => {
    try {
      await post(`ged/rh/integrations/${encodeURIComponent(id)}/pochette`, { cle, remise });
      await get().loadRh();
      return true;
    } catch (e) {
      get().showToast(e instanceof Error ? e.message : 'Enregistrement refusé', 'erreur');
      return false;
    }
  },

  /* Le RT ÉLAGUE la proposition déduite de la fonction. Sans ce geste, un
     dossier s'ouvrirait avec seize habilitations et se refermerait toujours
     incomplet. */
  retenirHabilitations: async (id, codes) => {
    try {
      await post(`ged/rh/integrations/${encodeURIComponent(id)}/habilitations`, { codes });
      await get().loadRh();
      get().showToast(`Programme arrêté à ${codes.length} habilitation(s).`);
      return true;
    } catch (e) {
      get().showToast(e instanceof Error ? e.message : 'Enregistrement refusé', 'erreur');
      return false;
    }
  },

  cloturerIntegration: async (id, conclusion, rouvrir) => {
    try {
      await post(`ged/rh/integrations/${encodeURIComponent(id)}/cloturer`, { conclusion, rouvrir });
      await get().loadRh();
      get().showToast(rouvrir ? 'Dossier rouvert.' : 'Dossier clôturé.');
      return true;
    } catch (e) {
      get().showToast(e instanceof Error ? e.message : 'Clôture refusée', 'erreur');
      return false;
    }
  },

  deposerPiece: async (orgId, cle, libelle, f) => {
    try {
      // SEC-10 b — ce dépôt n'avait AUCUNE borne, ni ici ni au serveur. Une
      // pièce du dossier est une image ou un PDF ; le contenu fait foi.
      const refus = await refusDepot('pieceRh', f, 'La pièce');
      if (refus) throw new Error(refus);
      const fichierData = await enBase64(f);
      await post(`ged/rh/personnel/${encodeURIComponent(orgId)}/pieces`,
        { cle, libelle, fichierNom: f.name, fichierData });
      /* `loadRh` parce que la liste des parcours porte encore les pièces : le
         parcours d'intégration les affiche, et sans ce rechargement il montre
         l'état d'avant le dépôt. Le dossier RH, lui, relit par `onDepose`. */
      await get().loadRh();
      get().showToast('Pièce déposée.');
      return true;
    } catch (e) {
      get().showToast(e instanceof Error ? e.message : 'Dépôt refusé', 'erreur');
      return false;
    }
  },

  /* Lue À LA DEMANDE, par personne : la liste globale des parcours ne couvre
     que ceux qui en ont un, et c'est précisément le trou qu'on referme. */
  chargerPiecesPersonne: async (orgId) => {
    try {
      const d = await api(`ged/rh/personnel/${encodeURIComponent(orgId)}/pieces`);
      return (d?.pieces ?? []) as RhIntegrationPiece[];
    } catch (e) {
      /* ⚠️ `null` ET NON `[]` : un refus de lecture et un dossier sans pièce ne
         se racontent pas de la même façon. Le toast dit la panne, et l'écran
         distingue les deux. */
      get().showToast(e instanceof Error ? e.message : 'Lecture des pièces refusée', 'erreur');
      return null;
    }
  },

  // Volontairement SANS rechargement de la liste : cet appel sert à ouvrir un
  // document, pas à modifier l'état. Chaque passage est journalisé côté serveur.
  consulterPiece: async (pieceId) => {
    try {
      const d = await api(`ged/rh/pieces/${encodeURIComponent(pieceId)}`);
      return { fichierNom: d?.fichierNom || '', fichierData: d?.fichierData || '' };
    } catch (e) {
      get().showToast(e instanceof Error ? e.message : 'Consultation refusée', 'erreur');
      return null;
    }
  },

  supprimerPiece: async (pieceId) => {
    try {
      await del(`ged/rh/pieces/${encodeURIComponent(pieceId)}`);
      await get().loadRh();
      get().showToast('Pièce supprimée.');
      return true;
    } catch (e) {
      get().showToast(e instanceof Error ? e.message : 'Suppression refusée', 'erreur');
      return false;
    }
  },

  saveHabilitation: async (h) => {
    try {
      await post('ged/rh/habilitations', h);
      await get().loadRh();
      get().showToast('Habilitation enregistrée.');
      return true;
    } catch (e) {
      get().showToast(e instanceof Error ? e.message : 'Enregistrement refusé', 'erreur');
      return false;
    }
  },

  /* ⚠️ ARCHIVER, ET NON SUPPRIMER (relevé MAJ-09 du 26/08/2026).
     `deleteHabilitation` effaçait l'habilitation ET ses attributions — la
     preuve que telle personne était habilitée à tel geste à telle date, sur un
     objet qu'on produit en audit. Le moteur refuse désormais la suppression et
     renvoie une phrase ; c'est cette route-ci qui fait le geste, avec un motif.
     ⚠️ POST et non DELETE : le corps d'un DELETE est jeté en chemin, le motif
     n'arriverait jamais et la garde serait sans effet. */
  archiverHabilitation: async (id, motif) => {
    try {
      const r = await post(`ged/rh/habilitations/${encodeURIComponent(id)}/archiver`, { motif });
      await get().loadRh();
      const n = Number(r?.attributionsPreservees || 0);
      get().showToast(n
        ? `Habilitation archivée — ${n} attribution(s) PRÉSERVÉE(S) : elles disent ce qui était vrai.`
        : 'Habilitation archivée.');
      return true;
    } catch (e) {
      get().showToast(e instanceof Error ? e.message : 'Archivage refusé', 'erreur');
      return false;
    }
  },

  accorderHabilitation: async (d) => {
    try {
      await post('ged/rh/habilitations-detenues', d);
      await get().loadRh();
      get().showToast('Habilitation accordée.');
      return true;
    } catch (e) {
      get().showToast(e instanceof Error ? e.message : 'Attribution refusée', 'erreur');
      return false;
    }
  },

  retirerHabilitation: async (id) => {
    try {
      await del(`ged/rh/habilitations-detenues/${encodeURIComponent(id)}`);
      await get().loadRh();
      get().showToast('Habilitation retirée.');
      return true;
    } catch (e) {
      get().showToast(e instanceof Error ? e.message : 'Retrait refusé', 'erreur');
      return false;
    }
  },

  creerPersonnel: async (p) => {
    try {
      const rep = await post('ged/rh/personnel', p) as { compte?: ResultatCompte } | null;
      // `refresh` ET `loadRh` : une arrivée touche l'organigramme et le dossier.
      await Promise.all([get().refresh(), get().loadRh()]);
      const c = rep?.compte;
      /* QUATRE issues, quatre messages.
         ⚠️ IL Y EN AVAIT TROIS, ET LE COMMENTAIRE D'ALORS DÉCRIVAIT DÉJÀ LA
         QUATRIÈME : « "Personne créée" alors que l'invitation n'est pas partie
         serait le pire des trois : on croirait la personne joignable, et sa
         première diffusion se perdrait sans que rien ne le dise. » Le cas était
         vu, il n'était simplement pas DISTINGUABLE — le moteur envoyait en
         `fire-and-forget` et ne rendait aucun verdict. Il en rend un depuis le
         13/09/2026, et ce message est celui que le commentaire attendait. */
      if (c?.erreur) get().showToast(`Personne créée, mais SANS compte : ${c.erreur}`, 'erreur');
      else if (c?.cree && c.courrielEnvoye === false) {
        get().showToast(
          `Personne créée et compte ouvert — mais le courriel n’est PAS parti (${c.motifEchec || 'motif inconnu'}). `
          + `${c.email} n’a rien reçu et ne peut pas se connecter. Renvoyez l’invitation depuis `
          + 'Paramétrage → Utilisateurs une fois le service de courriel rétabli.',
          'erreur',
        );
      } else if (c?.cree) get().showToast(`Personne créée. Invitation envoyée à ${c.email} — elle choisira son mot de passe elle-même.`);
      else get().showToast('Personne créée, sans compte de connexion.');
      return { ok: true, compte: c };
    } catch (e) {
      get().showToast(e instanceof Error ? e.message : 'Création refusée', 'erreur');
      return null;
    }
  },

  profilPourFonction: async (fonction) => {
    try {
      return await api(`ged/rh/profil-compte?fonction=${encodeURIComponent(fonction)}`) as PropositionCompte;
    } catch {
      // Muet volontairement : ne pas savoir proposer n'empêche pas de créer la
      // personne, et un message d'erreur sur une SUGGESTION ferait croire à un
      // refus. L'écran retombe sur « à décider ».
      return null;
    }
  },

  chargerRapprochementComptes: async () => {
    try {
      return await api('ged/org/comptes') as RapprochementComptes;
    } catch (e) {
      get().showToast(e instanceof Error ? e.message : 'Rapprochement indisponible', 'erreur');
      return null;
    }
  },

  savePoste: async (p) => {
    try {
      await post('ged/rh/postes', p);
      await get().loadRh();
      get().showToast('Poste enregistré.');
      return true;
    } catch (e) {
      /* ⚠️ 409 = CONFLIT, pas panne (05/10/2026) : le moteur refuse une couleur
         déjà portée par un autre poste actif — deux pastilles identiques
         rendraient le planning illisible. On le dit en clair et on garde la
         fiche ouverte, saisie intacte, pour en choisir une autre. Même
         traitement que la fiche du personnel (`majPersonnelRh`). */
      if (e instanceof ErreurAppel && e.statut === 409 && /couleur/i.test(e.message)) {
        get().showToast(`${e.message} La fiche reste ouverte : rien n’est perdu.`, 'erreur');
        return false;
      }
      get().showToast(e instanceof Error ? e.message : 'Enregistrement refusé', 'erreur');
      return false;
    }
  },

  /**
   * Retirer un poste — DÉSACTIVATION, pas effacement.
   *
   * ⚠️ Le moteur conserve les affectations passées : qui tenait quelle paillasse
   * tel jour est exactement ce qu'un audit vient vérifier. Une suppression avec
   * historique existe côté moteur, gardée par trois conditions ; elle ne
   * s'atteint pas depuis cet écran, et c'est voulu.
   */
  retirerPoste: async (code, motif) => {
    try {
      const r = await post(`ged/rh/postes/${encodeURIComponent(code)}/retirer`, { motif });
      await get().loadRh();
      get().showToast((r as { message?: string })?.message || 'Poste retiré.');
      return true;
    } catch (e) {
      get().showToast(e instanceof Error ? e.message : 'Retrait refusé', 'erreur');
      return false;
    }
  },

  // Rend le MOTIF du refus au lieu d'un simple échec : l'écran doit pouvoir
  // dire quelle habilitation manque, sinon la personne ne sait pas quoi faire.
  affecter: async (a) => {
    try {
      await post('ged/rh/affectations', a);
      await get().loadRh();
      /* ⚠️ LA PROJECTION DE MAINTENANCE DÉPEND DU PLANNING, et elle est
         calculée au SERVEUR puis gardée en mémoire depuis la connexion. Le
         destinataire d'une maintenance préventive est la personne planifiée ce
         jour-là sur le poste qui porte l'appareil : affecter quelqu'un change
         donc la projection, mais rien ne la rechargeait.

         Le tableau de bord continuait d'afficher « personne n'est planifié au
         poste dont relève cet appareil » — c'est-à-dire de réclamer très
         exactement le geste qu'on venait de faire — jusqu'à la reconnexion
         suivante. Un écran périmé qui donne un ordre ne se distingue pas d'un
         écran juste. (Constat de la RQ, 28/08/2026 : Manar Naamoun affectée sur
         P5_BACTERIO, maintenance de l'UriSed toujours sans titulaire.)

         `ressources/Maintenances.tsx` faisait déjà ce rechargement après un
         acquittement, pour la même raison. */
      await get().loadMaintenances();
      get().showToast(a.derogationMotif ? 'Affectation créée SOUS DÉROGATION — tracée au journal.' : 'Affectation créée.');
      return null;
    } catch (e) {
      const msg = e instanceof Error ? e.message : 'Affectation refusée';
      get().showToast(msg, 'erreur');
      return msg;
    }
  },

  // ---- Lot 2 --------------------------------------------------------------
  mesAbsences: [],
  rhAbsences: [],
  rhParametres: null,
  rhHabilitations: [],
  rhDetenues: [],
  rhContrats: [],
  rhIntegrations: [],
  rhPhasesReference: [],
  rhPochetteReference: [],
  rhFeries: [],
  joursChomesDuMois: (mois) => get().rhFeries.filter((f) => f.date.startsWith(`${mois}-`)).map((f) => f.date),
  loadCommentairePlanning: async (mois) => {
    const d = await api(`ged/rh/planning/commentaire?mois=${mois}`).catch(() => null);
    return d?.commentaire ?? null;
  },
  enregistrerCommentairePlanning: async (mois, texte) => {
    try {
      const d = await post('ged/rh/planning/commentaire', { mois, texte });
      return { ok: true, commentaire: d?.commentaire ?? null };
    } catch {
      return { ok: false, commentaire: null };
    }
  },
  rhRegimes: [],
  rhSoldes: [],
  rhAnneeGestion: null,

  loadMesAbsences: async () => {
    const d = await api('ged/rh/mes-absences').catch(() => null);
    set({ mesAbsences: d?.absences ?? [] });
  },

  loadRhGestion: async (annee) => {
    if (!get().rhAcces?.acces) return;
    const an = annee || get().rhAnneeGestion || new Date().getFullYear();
    set((s) => ({ rhChargement: s.rhChargement + 1, rhAnneeGestion: an }));
    try {
      const [parametres, absences, feries, soldes, regimes] = await Promise.all([
        api('ged/rh/parametres').then((d) => d?.parametres ?? null).catch(() => null),
        api(`ged/rh/absences?du=${an}-01-01&au=${an}-12-31`).then((d) => d?.absences ?? []).catch(() => []),
        api('ged/rh/feries').then((d) => d?.feries ?? []).catch(() => []),
        api(`ged/rh/compteurs?annee=${an}`).then((d) => d?.soldes ?? []).catch(() => []),
        api('ged/rh/periodes').then((d) => d?.periodes ?? []).catch(() => []),
      ]);
      set({ rhParametres: parametres, rhAbsences: absences, rhFeries: feries, rhSoldes: soldes, rhRegimes: regimes });
    } finally {
      set((s) => ({ rhChargement: s.rhChargement - 1 }));
    }
  },

  demanderAbsence: async (a) => {
    try {
      const r = await post('ged/rh/absences', a);
      await Promise.all([get().loadMesAbsences(), get().loadRhGestion()]);
      if (r?.alerte) get().showToast(`Absence enregistrée, mais attention — ${r.alerte}`, 'erreur');
      else get().showToast(a.personnelOrgId ? 'Absence enregistrée.' : 'Demande déposée.');
      return true;
    } catch (e) {
      get().showToast(e instanceof Error ? e.message : 'Demande refusée', 'erreur');
      return false;
    }
  },

  deciderAbsence: async (id, decision, motifDecision, transfererAuRemplacant) => {
    try {
      const r = await post(`ged/rh/absences/${id}/decision`, { decision, motifDecision, transfererAuRemplacant });
      /* ⚠️ `loadRh` ET PAS SEULEMENT `loadRhGestion` : valider un congé peut
         DÉPLACER des affectations vers le remplaçant, et `loadRhGestion` ne
         recharge pas les affectations — les cases seraient restées à l'ancien
         nom jusqu'au prochain changement de semaine. */
      await Promise.all([get().loadMesAbsences(), get().loadRhGestion(), get().loadRh()]);
      // Le moteur valide MAIS alerte quand le décompte fait passer le solde en
      // négatif : l'avertissement doit se voir, pas se perdre dans un toast vert.
      if (r?.alerte) get().showToast(`Absence validée, mais attention — ${r.alerte}`, 'erreur');
      else get().showToast(decision === 'validee' ? 'Absence validée.' : decision === 'refusee' ? 'Demande refusée — le motif est communiqué.' : 'Absence annulée.');

      /* ── LE PLANNING QUE L'ABSENCE LAISSE DÉCOUVERT ────────────────────────
         Le serveur ne retire jamais les cases tout seul : il les NOMME. Le
         valideur doit savoir ce qu'il laisse derrière lui — un congé validé
         sans reprise du planning, c'est un poste qui paraît tenu et ne l'est
         pas. */
      const t = r?.transferts;
      if (t) {
        if (t.faits?.length) get().showToast(`${t.faits.length} journée(s) transférée(s) à ${t.vers}.`);
        if (t.refuses?.length) {
          get().showToast(`${t.refuses.length} journée(s) NON transférée(s) — ${t.vers} n’est pas habilité(e) : ${t.refuses[0]}`, 'erreur');
        }
      }
      const reste = (r?.conflitsPlanning || []) as { date: string; posteCode: string }[];
      if (decision === 'validee' && reste.length) {
        get().showToast(
          `⚠ ${reste.length} journée(s) de planning restent au nom de la personne absente `
          + `(${reste.slice(0, 3).map((c) => `${c.date} ${c.posteCode}`).join(' · ')}${reste.length > 3 ? '…' : ''}). `
          + 'Elles apparaissent en conflit sur le planning.',
          'erreur',
        );
      }
      return true;
    } catch (e) {
      get().showToast(e instanceof Error ? e.message : 'Décision refusée', 'erreur');
      return false;
    }
  },

  saveRhParametres: async (p, base) => {
    try {
      /* `base` (14/10/2026) : les règles telles que l'écran les avait lues —
         le serveur n'écrit que ce qui a changé depuis (fusion à trois voies). */
      await post('ged/rh/parametres', base ? { ...p, base } : p);
      await get().loadRhGestion();
      get().showToast('Paramétrage RH enregistré.');
      return true;
    } catch (e) {
      get().showToast(e instanceof Error ? e.message : 'Enregistrement refusé', 'erreur');
      return false;
    }
  },

  saveFerie: async (f) => {
    try {
      await post('ged/rh/feries', f);
      await get().loadRhGestion();
      get().showToast('Jour férié enregistré.');
      return true;
    } catch (e) {
      get().showToast(e instanceof Error ? e.message : 'Enregistrement refusé', 'erreur');
      return false;
    }
  },

  /**
   * Déclarer ou modifier une période de régime spécial.
   *
   * Recharge le PLANNING autant que le paramétrage : une période change ce que
   * chaque poste-jour vaut, et laisser la grille sur ses anciens états
   * afficherait des écarts que le serveur ne reconnaît plus.
   */
  saveRegime: async (r) => {
    try {
      await post('ged/rh/periodes', r);
      await Promise.all([get().loadRhGestion(), get().loadRh()]);
      get().showToast(`Période « ${r.libelle} » enregistrée.`);
      return true;
    } catch (e) {
      get().showToast(e instanceof Error ? e.message : 'Période refusée', 'erreur');
      return false;
    }
  },

  supprimerRegime: async (documentId) => {
    try {
      await del(`ged/rh/periodes/${documentId}`);
      await Promise.all([get().loadRhGestion(), get().loadRh()]);
      get().showToast('Période retirée.');
      return true;
    } catch (e) {
      get().showToast(e instanceof Error ? e.message : 'Suppression refusée', 'erreur');
      return false;
    }
  },

  supprimerFerie: async (id) => {
    try {
      await api(`ged/rh/feries/${id}`, { method: 'DELETE' });
      await get().loadRhGestion();
      get().showToast('Jour férié retiré.');
      return true;
    } catch (e) {
      get().showToast(e instanceof Error ? e.message : 'Retrait refusé', 'erreur');
      return false;
    }
  },

  // ---- Lot 3 : présences --------------------------------------------------
  rhJournees: [],
  rapportImport: null,

  loadPresences: async (lundi) => {
    if (!get().rhAcces?.acces) return;
    const jours = joursSemaine(lundi || get().rhSemaine);
    set((s) => ({ rhChargement: s.rhChargement + 1 }));
    try {
      const d = await api(`ged/rh/journees?du=${jours[0]}&au=${jours[6]}`).catch(() => null);
      set({ rhJournees: d?.journees ?? [] });
    } finally {
      set((s) => ({ rhChargement: s.rhChargement - 1 }));
    }
  },

  /* Pointage BRUT — `GET ged/rh/pointages`.
   *
   * ⚠️ Route autorisée depuis sa livraison, mais qu'AUCUN écran n'atteignait :
   * la preuve existait et personne ne pouvait la regarder. C'est le geste du
   * lot GED-15 b. L'unique occurrence du chemin dans ce fichier était
   * `ged/rh/pointages/import` — l'import, pas la consultation ; deux routes
   * dont l'une est le préfixe de l'autre, ce qui a longtemps fait croire à un
   * appelant (cf. qa/AVANCEMENT-GED-15.md §4).
   *
   * Chargée À LA DEMANDE, quand on ouvre le brut, pas au montage de l'écran :
   * une semaine de pointages est beaucoup plus volumineuse que les journées
   * reconstituées, et neuf consultations sur dix ne le demandent pas.
   * `rhPointagesCharges` retient le lundi servi pour ne pas relire deux fois. */
  rhPointages: [],
  rhPointagesCharges: null,

  rhCouvertures: null,
  rhTrousImport: [],
  rhControle: null,

  rhSensSuspects: null,
  rhRecap: null,
  loadDetailPresences: async (mois, orgId) => {
    if (!get().rhAcces?.acces) return null;
    return api(`ged/rh/presences/detail?mois=${mois}&personne=${encodeURIComponent(orgId)}`).catch(() => null);
  },
  loadRecapPresences: async (mois) => {
    if (!get().rhAcces?.acces) return;
    set((s) => ({ rhChargement: s.rhChargement + 1 }));
    try {
      const d = await api(`ged/rh/presences/recap?mois=${mois}`).catch(() => null);
      if (d) set({ rhRecap: { mois, lignes: d.lignes ?? [] } });
    } finally {
      set((s) => ({ rhChargement: s.rhChargement - 1 }));
    }
  },
  loadSensSuspects: async (du, au) => {
    if (!get().rhAcces?.acces) return;
    const d = await api(`ged/rh/pointages/sens-suspects?du=${du}&au=${au}`).catch(() => null);
    if (d) set({ rhSensSuspects: { du, au, suggestions: d.suggestions ?? [] } });
  },

  verifierPointage: async (id, statut, motif, sens, silencieux) => {
    try {
      await post(`ged/rh/pointages/${id}/verifier`, { statut, motif, ...(sens ? { sens } : {}) });
      /* En lot, un seul rechargement à la fin — l'appelant s'en charge. */
      if (silencieux) return true;
      /* Le brut mémorisé porte l'ancienne vérification : on le relit. */
      set({ rhPointagesCharges: null });
      await Promise.all([get().loadPresences(), get().loadPointages()]);
      const k = get().rhControle;
      if (k) void get().loadControle(k.du, k.au);
      const ks = get().rhSensSuspects;
      if (ks) void get().loadSensSuspects(ks.du, ks.au);
      get().showToast(statut === 'sens' ? 'Sens corrigé — le passage d’origine reste visible au pointage brut.'
        : statut === 'confirme' ? 'Passage confirmé — la réserve est levée.'
        : statut === 'ecarte' ? 'Passage écarté — il reste visible au pointage brut, mais ne compte plus.'
          : 'Vérification retirée.');
      return true;
    } catch (e) {
      /* UNE JOURNÉE VALIDÉE BLOQUE (RQ, 01/10/2026 : « facilite-moi la
         dévalidation via un pop-up ; j'ai juste à confirmer ») : on propose de
         la rouvrir, puis on refait la vérification demandée. */
      const validees = ((e as { details?: { validees?: { documentId: string; date: string }[] } }).details?.validees) || [];
      if (validees.length && typeof window !== 'undefined') {
        const dates = validees.map((v) => v.date.split('-').reverse().join('/')).join(', ');
        if (await get().demanderConfirmation(`La journée du ${dates} est déjà validée.\n\nLa rouvrir pour corriger ce passage ? Elle redeviendra « à valider » et sera recalculée ; la réouverture est tracée au journal.`, { titre: 'Rouvrir la journée', confirmer: 'Rouvrir' })) {
          try {
            for (const v of validees) await post(`ged/rh/journees/${v.documentId}/rouvrir`, {});
            return await get().verifierPointage(id, statut, motif, sens, silencieux);
          } catch (e2) {
            get().showToast(e2 instanceof Error ? e2.message : 'Réouverture refusée', 'erreur');
            return false;
          }
        }
        return false;
      }
      get().showToast(e instanceof Error ? e.message : 'Vérification refusée', 'erreur');
      return false;
    }
  },

  appliquerPropositionAbsence: async (c, p, motif) => {
    const id = c.absenceId;
    if (!id) return false;
    try {
      let message = '';
      if (p.type === 'ajuster') {
        const r = await post(`ged/rh/absences/${id}/ajuster`, { periodes: p.periodes, motif });
        message = r?.message || 'Absence corrigée.';
      } else if (p.type === 'annuler') {
        await post(`ged/rh/absences/${id}/decision`, { decision: 'annulee', motifDecision: motif });
        /* L'annulation ne relit pas les journées : sans ce recalcul, elles
           resteraient « absence contredite » alors qu'il n'y a plus d'absence. */
        const ds = c.dates.slice().sort();
        if (ds.length) await post('ged/rh/journees/recalcul', { du: ds[0], au: ds[ds.length - 1] });
        message = 'Absence annulée — les jours sont recrédités au solde, et les journées badgées redeviennent du travail.';
      } else {
        /* CONSERVER : l'absence est juste, le badge ne l'est pas. On ne touche
           pas au congé ; on retient 0 h sur les journées contredites, avec le
           motif — le pointage brut, lui, reste tel quel. */
        const ds = c.dates.slice().sort();
        const d = await api(`ged/rh/journees?du=${ds[0]}&au=${ds[ds.length - 1]}`);
        const ids = ((d?.journees || []) as RhJournee[])
          .filter((j) => j.personnel?.orgId === c.orgId && !j.validee && (j.anomalies || []).includes('absence_contredite'))
          .map((j) => j.documentId);
        if (!ids.length) throw new Error('Aucune journée à corriger : elles sont peut-être déjà validées.');
        await post('ged/rh/journees/correction-masse', { documentIds: ids, mode: 'heures', heures: 0, motif });
        message = `${ids.length} journée(s) ramenée(s) à 0 h — l’absence est conservée.`;
      }
      get().showToast(message);
      await get().loadPresences();
      const k = get().rhControle;
      if (k) await get().loadControle(k.du, k.au);
      return true;
    } catch (e) {
      get().showToast(e instanceof Error ? e.message : 'Correction refusée', 'erreur');
      return false;
    }
  },

  loadControle: async (du, au) => {
    if (!get().rhAcces?.acces) return;
    const d = await api(`ged/rh/pointages/controle?du=${du}&au=${au}`).catch(() => null);
    // Mémorisé seulement en cas de succès : une panne ne doit pas se lire
    // « aucune incohérence ».
    if (d) set({ rhControle: { du, au, constats: d.constats ?? [], passagesLus: Number(d.passagesLus ?? 0) } });
  },

  loadCouvertures: async () => {
    if (!get().rhAcces?.acces) return;
    const d = await api('ged/rh/pointages/couvertures').catch(() => null);
    // Comme pour le brut : on ne mémorise qu'en cas de succès. Un refus passager
    // ferait lire « aucune couverture » — c'est-à-dire, par la règle de
    // prudence du moteur, « tout est mesuré ». L'inverse de ce qu'on cherche.
    if (d) set({ rhCouvertures: d.couvertures ?? [], rhTrousImport: d.trous ?? [] });
  },

  loadPointages: async (lundi) => {
    if (!get().rhAcces?.acces) return;
    const semaine = lundi || get().rhSemaine;
    if (get().rhPointagesCharges === semaine) return;
    const jours = joursSemaine(semaine);
    set((s) => ({ rhChargement: s.rhChargement + 1 }));
    try {
      const d = await api(`ged/rh/pointages?du=${jours[0]}&au=${jours[6]}`).catch(() => null);
      // On ne mémorise la semaine QUE si la lecture a abouti : sinon un refus
      // passager ferait croire à un brut vide et interdirait toute relecture.
      if (d) set({ rhPointages: d.pointages ?? [], rhPointagesCharges: semaine });
    } finally {
      set((s) => ({ rhChargement: s.rhChargement - 1 }));
    }
  },

  rhJournal: null,

  // Relu à chaque ouverture de l'écran, sans mémorisation : un journal se
  // consulte pour savoir ce qui vient de se passer, et servir la copie d'il y a
  // vingt minutes serait la seule chose qu'il ne doit pas faire.
  loadJournalRh: async () => {
    set((s) => ({ rhChargement: s.rhChargement + 1 }));
    try {
      const d = await api('ged/rh/journal').catch(() => null);
      // Refus ou panne : on laisse `null`. Écrire `[]` dirait « aucun acte
      // enregistré » à quelqu'un à qui la lecture a simplement été refusée.
      if (d) set({ rhJournal: (d.events ?? []) as GedEvent[] });
    } finally {
      set((s) => ({ rhChargement: s.rhChargement - 1 }));
    }
  },

  // Le rapport d'import n'est PAS un toast : il porte les lignes rejetées et
  // les badges inconnus, c'est-à-dire des heures de travail qui manqueraient
  // sans que personne ne les réclame. Il reste à l'écran jusqu'à ce qu'on le ferme.
  importerPointages: async (contenu, mappage, classeurB64) => {
    try {
      const r = await post('ged/rh/pointages/import', { contenu, mappage, classeurB64 });
      // Un import ajoute du brut : la lecture mémorisée n'est plus à jour.
      set({ rapportImport: r, rhPointages: [], rhPointagesCharges: null });
      /* La couverture a changé : sans la relire, l'écran continuait d'afficher
         « aucun fichier importé pour cette période » juste après l'import de
         cette période (constaté le 29/09/2026). Le contrôle aussi. */
      await Promise.all([get().loadPresences(), get().loadCouvertures()]);
      const c = get().rhControle;
      if (c) void get().loadControle(c.du, c.au);
      /* Le récapitulatif du mois se relit aussi : sinon il montrait les
         chiffres d'avant le recalcul (RQ, 30/09/2026). */
      const rc = get().rhRecap;
      if (rc) void get().loadRecapPresences(rc.mois);
      return true;
    } catch (e) {
      get().showToast(e instanceof Error ? e.message : 'Import refusé', 'erreur');
      return false;
    }
  },

  effacerRapportImport: () => set({ rapportImport: null }),
  annulerImportPointage: async (lot, motif) => {
    try {
      const r = await post(`ged/rh/pointages/imports/${encodeURIComponent(lot)}/annuler`, { motif });
      set({ rapportImport: null, rhPointages: [], rhPointagesCharges: null });
      await Promise.all([get().loadPresences(), get().loadCouvertures()]);
      const c = get().rhControle;
      if (c) void get().loadControle(c.du, c.au);
      /* Le récapitulatif du mois se relit aussi : sinon il montrait les
         chiffres d'avant le recalcul (RQ, 30/09/2026). */
      const rc = get().rhRecap;
      if (rc) void get().loadRecapPresences(rc.mois);
      get().showToast(`Import annulé : ${r?.passages ?? 0} passage(s) et ${r?.journees ?? 0} journée(s) effacés.`);
      return true;
    } catch (e) {
      get().showToast(e instanceof Error ? e.message : 'Annulation refusée', 'erreur');
      return false;
    }
  },

  validerHeuresSup: async (id, heures, motif) => {
    try {
      await post(`ged/rh/journees/${id}/heures-sup`, { heures, motif });
      await get().loadPresences();
      const rc = get().rhRecap;
      if (rc) void get().loadRecapPresences(rc.mois);
      get().showToast(heures > 0 ? 'Heures supplémentaires validées.' : 'Validation des heures supplémentaires retirée.');
      return true;
    } catch (e) {
      get().showToast(e instanceof Error ? e.message : 'Validation refusée', 'erreur');
      return false;
    }
  },
  corrigerJournee: async (id, heuresRetenues, motifEcart, reel) => {
    try {
      await post(`ged/rh/journees/${id}/correction`, {
        heuresRetenues, motifEcart,
        /* Heures réelles déclarées (30/09/2026) : envoyées même vides, pour
           qu'une déclaration retirée le soit aussi en base. */
        arriveeDeclaree: reel?.arrivee ?? '', departDeclare: reel?.depart ?? '',
      });
      await get().loadPresences();
      get().showToast('Correction enregistrée — le pointage d’origine est conservé.');
      return true;
    } catch (e) {
      get().showToast(e instanceof Error ? e.message : 'Correction refusée', 'erreur');
      return false;
    }
  },

  corrigerJourneesEnMasse: async (documentIds, mode, motif, heures) => {
    try {
      const r = await post('ged/rh/journees/correction-masse', { documentIds, mode, motif, heures });
      await get().loadPresences();
      const ig = Array.isArray(r?.ignorees) ? r.ignorees : [];
      // Les écartées sont NOMMÉES : un geste de masse qui saute des lignes en
      // silence ferait croire à une correction complète.
      get().showToast(
        `${r?.corrigees ?? 0} journée(s) corrigée(s).`
        + (ig.length ? ` ${ig.length} écartée(s) : ${ig.slice(0, 3).map((x: { ref: string; raison: string }) => `${x.ref} (${x.raison})`).join(' · ')}${ig.length > 3 ? '…' : ''}` : ''),
        ig.length ? 'erreur' : 'info',
      );
      return true;
    } catch (e) {
      get().showToast(e instanceof Error ? e.message : 'Correction refusée', 'erreur');
      return false;
    }
  },

  loadJourneesPersonne: async (du, au, orgId) => {
    const d = await api(`ged/rh/journees?du=${du}&au=${au}`).catch(() => null);
    return ((d?.journees || []) as RhJournee[]).filter((j) => j.personnel?.orgId === orgId);
  },

  validerPresences: async (du, au, forcer, responsable, personnelOrgId) => {
    try {
      const r = await post('ged/rh/journees/valider', { du, au, forcer, responsable, ...(personnelOrgId ? { personnelOrgId } : {}) });
      await get().loadPresences();
      /* Les journées modifiées PENDANT la validation ne sont plus validées sur
         leur ancienne lecture (14/10/2026) : le serveur les écarte et les nomme. */
      const ecartees = Array.isArray(r?.ecartees) ? (r.ecartees as string[]) : [];
      get().showToast(`${r?.validees ?? 0} journée(s) validée(s).`
        + (ecartees.length ? ` ${ecartees.length} écartée(s), modifiée(s) pendant la validation — relisez-les puis revalidez : ${ecartees.slice(0, 3).join(', ')}${ecartees.length > 3 ? '…' : ''}.` : ''),
        ecartees.length ? 'erreur' : undefined);
      return true;
    } catch (e) {
      get().showToast(e instanceof Error ? e.message : 'Validation refusée', 'erreur');
      return false;
    }
  },

  /* Recalcul des journées d'une plage — `POST ged/rh/journees/recalcul`.
   *
   * ⚠️ Route autorisée depuis sa livraison sans qu'aucun écran ne l'atteigne
   * (GED-15 c). Ce qu'elle répare n'a pourtant aucun autre remède : les
   * journées ne sont reconstituées qu'à l'IMPORT d'un fichier de badgeuse
   * (`importPointages` appelle le même calcul en fin de course). Corriger le
   * planning ou valider une absence APRÈS l'import laisse donc l'écart et
   * l'état de la journée figés sur un planifié qui n'existe plus — le
   * gestionnaire voyait une anomalie qu'il ne pouvait pas faire disparaître
   * autrement qu'en réimportant le fichier.
   *
   * ⚠️ Ce que le moteur ne touche PAS, et c'est ce qui rend le geste sûr :
   * une journée VALIDÉE est sautée (rendue dans `ignoreesValidees`), et une
   * correction — heures retenues, motif, auteur — survit au recalcul, parce
   * que l'écriture est un patch partiel qui ne porte que le pointé, le
   * planifié, l'écart, l'état et les anomalies. */
  recalculerPresences: async (du, au) => {
    try {
      const r = await post('ged/rh/journees/recalcul', { du, au });
      await get().loadPresences();
      const c = get().rhControle;
      if (c) void get().loadControle(c.du, c.au);
      /* Le récapitulatif du mois se relit aussi : sinon il montrait les
         chiffres d'avant le recalcul (RQ, 30/09/2026). */
      const rc = get().rhRecap;
      if (rc) void get().loadRecapPresences(rc.mois);
      const inconnus = (r?.badgesInconnus ?? []) as string[];
      get().showToast(
        `${r?.calculees ?? 0} journée(s) recalculée(s)`
        + `${r?.ignoreesValidees ? ` · ${r.ignoreesValidees} déjà validée(s), laissée(s) intacte(s)` : ''}`
        + `${inconnus.length ? ` · badges sans salarié : ${inconnus.join(', ')}` : ''}`,
      );
      return true;
    } catch (e) {
      get().showToast(e instanceof Error ? e.message : 'Recalcul refusé', 'erreur');
      return false;
    }
  },

  rouvrirJournee: async (id) => {
    try {
      await post(`ged/rh/journees/${id}/rouvrir`, {});
      await get().loadPresences();
      get().showToast('Journée rouverte.');
      return true;
    } catch (e) {
      get().showToast(e instanceof Error ? e.message : 'Réouverture refusée', 'erreur');
      return false;
    }
  },

  rhPaie: null,
  rhPeriode: moisCourant(),
  setRhPeriode: (p) => { set({ rhPeriode: p }); void get().loadPaie(p); },

  loadPaie: async (periode) => {
    if (!get().rhAcces?.acces) return;
    const p = periode || get().rhPeriode;
    set((s) => ({ rhChargement: s.rhChargement + 1 }));
    try {
      const d = await api(`ged/rh/paie?periode=${p}`).catch(() => null);
      set({ rhPaie: d, rhPeriode: p });
    } finally {
      set((s) => ({ rhChargement: s.rhChargement - 1 }));
    }
  },

  cloturerPaie: async (periode, forcer, responsable) => {
    try {
      const r = await post('ged/rh/paie/cloturer', { periode, forcer, responsable });
      await get().loadPaie(periode);
      get().showToast(`${periode} clôturé — empreinte ${r?.empreinte}. L'export est désormais reproductible.`);
      return true;
    } catch (e) {
      get().showToast(e instanceof Error ? e.message : 'Clôture refusée', 'erreur');
      return false;
    }
  },

  // Le téléchargement se fait ici et non par un lien : le contenu vient d'une
  // route à droits, pas d'un fichier public.
  exporterPaie: async (periode) => {
    try {
      const d = await api(`ged/rh/paie/export?periode=${periode}`);
      const blob = new Blob([d.contenu], { type: 'text/csv;charset=utf-8' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = d.fichier;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      get().showToast(d.avertissement || `Export ${periode} téléchargé — empreinte ${d.empreinte}.`, d.avertissement ? 'erreur' : 'info');
      return true;
    } catch (e) {
      get().showToast(e instanceof Error ? e.message : 'Export refusé', 'erreur');
      return false;
    }
  },

  regulariserPaie: async (r) => {
    try {
      const d = await post('ged/rh/paie/regularisation', r);
      await get().loadPaie(r.periode);
      get().showToast(`Régularisation enregistrée — elle sera transmise avec ${d?.reporteeSur}.`);
      return true;
    } catch (e) {
      get().showToast(e instanceof Error ? e.message : 'Régularisation refusée', 'erreur');
      return false;
    }
  },

  majPersonnelRh: async (orgId, champs) => {
    try {
      await post(`ged/rh/personnel/${orgId}`, champs);
      /* `refresh` ET `loadRh`, comme pour une arrivée : depuis le 01/09/2026 la
         fiche modifie aussi l'IDENTITÉ (nom, fonction, case, niveau), et ces
         champs-là s'affichent depuis `settings.gedUsers` — la vue recomposée.
         Ne recharger que `rhPersonnel` écrivait le bon nom en base et laissait
         l'ancien à l'écran. */
      await Promise.all([get().refresh(), get().loadRh()]);
      get().showToast('Fiche mise à jour.');
      return true;
    } catch (e) {
      /* ⚠️ 409 = CONFLIT, pas panne : le moteur refuse une couleur déjà portée
         par un collègue de même fonction (deux pastilles identiques rendraient le
         planning illisible). On le dit en clair et on garde la fiche ouverte,
         saisie intacte, pour en choisir une autre. */
      if (e instanceof ErreurAppel && e.statut === 409 && /couleur/i.test(e.message)) {
        get().showToast(`${e.message} La fiche reste ouverte : rien n’est perdu.`, 'erreur');
        return false;
      }
      get().showToast(e instanceof Error ? e.message : 'Mise à jour refusée', 'erreur');
      return false;
    }
  },

  retirerAffectation: async (documentId) => {
    try {
      await api(`ged/rh/affectations/${documentId}`, { method: 'DELETE' });
      await get().loadRh();
      // Symétrique de `affecter` : retirer quelqu'un rend la maintenance de son
      // appareil sans titulaire, et l'écran doit le dire tout de suite.
      await get().loadMaintenances();
      get().showToast('Affectation retirée.');
      return true;
    } catch (e) {
      get().showToast(e instanceof Error ? e.message : 'Retrait refusé', 'erreur');
      return false;
    }
  },

  derogerAbsence: async (affectationId, motif, responsable) => {
    try {
      await post(`ged/rh/affectations/${affectationId}/deroger-absence`, { motif, responsable });
      await get().loadRh();
      get().showToast('Dérogation enregistrée — le conflit reste visible, marqué.');
      return null;
    } catch (e) {
      const msg = e instanceof Error ? e.message : 'Dérogation refusée';
      get().showToast(msg, 'erreur');
      return msg;
    }
  },

  saveSettings: async (patch, versions) => {
    try {
      /* Seules les versions des réglages ENVOYÉS partent : le serveur ne
         contrôle que ceux-là, et un réglage non renvoyé n'est pas réécrit. */
      const lues = versions
        ? Object.fromEntries(Object.keys(patch).filter((k) => versions[k]).map((k) => [k, versions[k]]))
        : undefined;
      await api('ged-setting', {
        method: 'PUT', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ data: patch, ...(lues ? { versions: lues } : {}) }),
      });
      await get().refresh();
      get().showToast('Paramétrage enregistré.');
      return true;
    } catch (e) {
      get().showToast(e instanceof Error ? e.message : 'Échec de l’enregistrement', 'erreur');
      return false;
    }
  },
}));

// ---- sélecteurs utilitaires ----
/**
 * NOMMER LA PERSONNE DERRIÈRE UNE CLÉ — « Dr Inès Harzallah », jamais
 * « admin.harzallah ».
 *
 * Partout dans la base, ce qu'une trace retient d'une personne est sa CLÉ
 * TECHNIQUE (`rh-personnel.orgId`) : elle est immuable, et c'est pour cela
 * qu'on la stocke (cf. `backend/src/identite-compte.ts`). Mais la clé encode
 * le rôle du jour où elle a été créée et ment vite : « coord.ezzi » se lit
 * encore sur les écrans alors qu'Asma n'est plus coordinatrice, et
 * « tech.belhadjsalah » désigne Wafa Hadj Hassine. Elle ne doit JAMAIS
 * atteindre l'écran.
 *
 * Deux sources, dans cet ordre :
 *  1. `gedUsers` — la vue de l'organigramme, qui porte aussi les comptes sans
 *     fiche RH (confrères, recette) ;
 *  2. `acteurs` — l'annuaire COMPLET servi par `/ged-setting`, PARTANTS
 *     COMPRIS. ⚠️ Il est indispensable : la vue écarte délibérément qui a
 *     quitté ses fonctions (arbitrage du 27/08/2026), si bien qu'un acte
 *     ancien — 186 calibrations au 11/10/2026 — se nommait « tech.chaibi ».
 *
 * ⚠️ Le dernier repli rend la CLÉ, et c'est voulu : une clé lisible vaut mieux
 * qu'un tiret muet quand l'acteur n'est personne (`systeme`, `reprise`). Mais
 * ce repli est précisément ce qui rendait le défaut silencieux — si une clé
 * s'affiche encore, c'est que la personne manque aux DEUX sources, pas que cet
 * écran a oublié d'appeler `userName`.
 */
export function userName(settings: GedSettings | null, id: string | null | undefined): string {
  /* ⚠️ `null` et `undefined` sont ACCEPTÉS, et c'est volontaire : les champs
     d'acteur sont optionnels dans la moitié des routes, et obliger chaque
     appelant à écrire `x.par || ''` est exactement l'oubli qu'on vient de
     corriger trente fois. Une clé absente se lit « — ». */
  if (!id) return '—';
  const vue = settings?.gedUsers?.find((u) => u.id === id)?.nom;
  if (vue) return vue;
  return settings?.acteurs?.[id] || id;
}
export function userInitials(settings: GedSettings | null, id: string | null | undefined): string {
  const u = settings?.gedUsers?.find((x) => x.id === id);
  if (u?.initiales) return u.initiales;
  /* Un partant n'est plus dans la vue : ses initiales se composent depuis
     l'annuaire (« Zayd Chaibi » → ZC), et non sur les deux premières lettres
     de la clé, qui donnerait « TE » pour tout le monde chez les techniciens. */
  const nom = id ? settings?.acteurs?.[id] : '';
  if (nom) {
    const mots = nom.replace(/^(Dr|Pr|Mme|M\.|Mlle)\s+/i, '').split(/\s+/).filter(Boolean);
    if (mots.length) return (mots[0][0] + (mots[1]?.[0] || '')).toUpperCase();
  }
  return (id || '??').slice(0, 2).toUpperCase();
}
